import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import {
  clearIdentity,
  identifyUser,
  setGroup,
  trackEvent,
  upsertGroup,
} from "../analytics";
import {
  buildCardThemeFieldsFromPreset,
  buildDefaultCardThemeFields,
  cardThemeFonts,
  cardThemePresets,
  cardThemeRadiusOptions,
  getCardTheme,
  isValidHexColor,
} from "../lib/cardTheme";
import {
  getCardFeatureSnapshot,
  getChangedFeatures,
  getChangedFields,
} from "../lib/cardFeatureTracking";
import {
  clearEventAvatarAiSignupHandoff,
  loadEventAvatarAiSignupHandoff,
} from "../lib/eventAvatarAiSignupHandoff";
import { homeSeo, noIndexSeo, setPageSeo } from "../seo";
import cardsWhiteLogoUrl from "../assets/Logo - Cards - white.svg";
import AvatarAiModal from "./AvatarAiModal";
import BuyCreditsModal from "./BuyCreditsModal";
import CardToolsPanel from "./tools/CardToolsPanel";
import VatLookupDialog from "./VatLookupDialog";

const getApiBaseUrl = () => {
  const rawUrl = process.env.REACT_APP_CARDS_API_URL || "";

  if (!rawUrl) {
    return "";
  }

  return rawUrl.startsWith("http") ? rawUrl : `https://${rawUrl}`;
};

const selfServiceTokenStorageKey = "cardsSelfServiceToken";
const sessionTokenQueryParams = [
  "token",
  "sessionToken",
  "magicToken",
  "access_token",
];
const loginCodeQueryParams = ["code", "otp", "loginCode"];
const imageUploadFields = new Set(["avatarUrl", "logoUrl"]);
const imageContentTypes = ["image/jpeg", "image/png", "image/webp"];
const maxImageUploadSize = 5 * 1024 * 1024;
const imageUploadSettings = {
  avatarUrl: {
    icon: "mdi:account-circle-outline",
    previewClassName: "h-20 w-20 rounded-full",
    emptyPreviewClassName: "rounded-full",
    imageClassName: "object-cover",
  },
  logoUrl: {
    icon: "mdi:image-filter-center-focus-strong-outline",
    previewClassName: "h-20 w-28 rounded-md",
    emptyPreviewClassName: "rounded-md",
    imageClassName: "object-contain",
  },
};

const normalizeCouponCode = (couponCode = "") => {
  const normalizedCode = couponCode.trim().toUpperCase();

  return normalizedCode === "OFC-TANEXPO26" ? "TANEXPO26" : normalizedCode;
};

const normalizeCouponDisplayMessage = (message = "") =>
  String(message)
    .replace(/Cards Plus gratuitamente a vita/gi, "Cards Plus gratuito")
    .replace(/Cards Plus gratis a vita/gi, "Cards Plus gratuito")
    .replace(/Cards Plus gratuitamente/gi, "Cards Plus gratuito");

const fairCouponCode = "TANEXPO26";

const getCouponEventProps = (coupon = {}, fallbackCode = "") => {
  const normalizedCode = normalizeCouponCode(coupon.code || fallbackCode);

  return {
    couponCampaign:
      normalizedCode === fairCouponCode ? "tanexpo_2026" : "other",
    ...(coupon.benefitType ? { couponBenefitType: coupon.benefitType } : {}),
    ...(coupon.plan ? { couponPlan: coupon.plan } : {}),
    ...(coupon.planStatus ? { couponPlanStatus: coupon.planStatus } : {}),
    ...(coupon.sponsorMode ? { couponSponsorMode: coupon.sponsorMode } : {}),
  };
};
const formatCouponDeadlineLabel = (expiresAt = "") => {
  const expiresAtTime = Date.parse(expiresAt);

  if (!Number.isFinite(expiresAtTime)) {
    return "";
  }

  const parts = new Intl.DateTimeFormat("it-IT", {
    timeZone: "Europe/Rome",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
    .formatToParts(new Date(expiresAtTime))
    .reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});

  return `${parts.day}/${parts.month}/${parts.year} alle ${parts.hour}:${parts.minute}`;
};

const heroMockupUrl = `${process.env.PUBLIC_URL}/mockup-hero.png`;
const howItWorksImageUrl = `${process.env.PUBLIC_URL}/Cards-come-funziona.png`;
const audienceImageUrl = `${process.env.PUBLIC_URL}/cards-Pensata%20per%20il%20tuo%20contesto.png`;
const plansImageUrl = `${process.env.PUBLIC_URL}/cards-fascia-piani.png`;
const signupImageUrl = `${process.env.PUBLIC_URL}/cards-fascia-form.png`;
const ofcLogoUrl = `${process.env.PUBLIC_URL}/ofc_logo.png`;
const annunciFunebriFairLogoUrl = `${process.env.PUBLIC_URL}/af-logo-simbolo-scuro.png`;
const privacyPolicyUrl = "/privacy-policy";
const termsUrl = "/termini-e-condizioni";
const ofcSocialLinks = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/onoranzefunebricloud/",
    icon: "mdi:facebook",
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/onoranzefunebricloud/",
    icon: "mdi:instagram",
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/channel/UCAft4XAHnb_DIavWF8s2pbw",
    icon: "mdi:youtube",
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/onoranzefunebricloud",
    icon: "mdi:linkedin",
  },
];
const getCouponCountdown = (expiresAt = "") => {
  const expiresAtTime = Date.parse(expiresAt);

  if (!Number.isFinite(expiresAtTime)) {
    return null;
  }

  const remainingMs = Math.max(0, expiresAtTime - Date.now());
  const totalMinutes = Math.floor(remainingMs / (1000 * 60));

  return {
    days: Math.floor(totalMinutes / (60 * 24)),
    hours: Math.floor((totalMinutes % (60 * 24)) / 60),
    minutes: totalMinutes % 60,
    expired: remainingMs <= 0,
  };
};

const getSearchParams = () => {
  if (typeof window === "undefined") {
    return new URLSearchParams();
  }

  return new URLSearchParams(window.location.search);
};

const getSafeHostname = (value = "") => {
  if (!value) return "";

  try {
    return new URL(value).hostname.replace(/^www\./i, "");
  } catch {
    return "";
  }
};

const getTrackingAttributionProps = () => {
  const params = getSearchParams();
  const props = {};

  ["utm_source", "utm_medium", "utm_campaign"].forEach((key) => {
    const value = params.get(key);
    if (value) {
      props[key] = value;
    }
  });

  if (typeof document !== "undefined") {
    const referrer = getSafeHostname(document.referrer);
    if (referrer) {
      props.referrer = referrer;
    }
  }

  return props;
};

const getUrlCouponCode = () => {
  const params = getSearchParams();

  return normalizeCouponCode(
    params.get("coupon") ||
      params.get("couponCode") ||
      params.get("codiceCoupon") ||
      "",
  );
};

const getInitialCouponCode = () =>
  getUrlCouponCode();

const getUrlSessionToken = () => {
  const params = getSearchParams();
  const tokenParam = sessionTokenQueryParams.find((param) => params.get(param));

  return tokenParam ? params.get(tokenParam).trim() : "";
};

const getUrlLoginCode = () => {
  const params = getSearchParams();
  const codeParam = loginCodeQueryParams.find((param) => params.get(param));

  return codeParam ? params.get(codeParam).trim() : "";
};

const getUrlWantsCodeEntry = () => {
  const params = getSearchParams();

  return (
    params.get("verify") === "1" ||
    params.get("step") === "code" ||
    params.get("otpRequired") === "1"
  );
};

const getStoredSessionToken = () => {
  if (typeof window === "undefined") {
    return "";
  }

  return window.sessionStorage.getItem(selfServiceTokenStorageKey) || "";
};

const storeSessionToken = (value) => {
  if (typeof window === "undefined" || !value) {
    return;
  }

  window.sessionStorage.setItem(selfServiceTokenStorageKey, value);
};

const clearStoredSessionToken = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(selfServiceTokenStorageKey);
};

const removeQueryParamsFromUrl = (paramsToRemove) => {
  if (typeof window === "undefined" || !window.history?.replaceState) {
    return;
  }

  const params = getSearchParams();
  const hadParams = paramsToRemove.some((param) => params.has(param));

  if (!hadParams) {
    return;
  }

  paramsToRemove.forEach((param) => params.delete(param));

  const queryString = params.toString();
  const nextUrl = `${window.location.pathname}${
    queryString ? `?${queryString}` : ""
  }${window.location.hash}`;

  window.history.replaceState(window.history.state, document.title, nextUrl);
};

const removeSessionTokenFromUrl = () =>
  removeQueryParamsFromUrl(sessionTokenQueryParams);

const removeLoginCodeFromUrl = () =>
  removeQueryParamsFromUrl(loginCodeQueryParams);

const buildManageCardUrl = ({
  token = "",
  email = "",
  requireCode = false,
} = {}) => {
  const params = new URLSearchParams();

  if (token) {
    params.set("token", token);
  } else if (email) {
    params.set("email", email.trim());
  }

  if (requireCode) {
    params.set("verify", "1");
  }

  const queryString = params.toString();

  return `/gestisci${queryString ? `?${queryString}` : ""}`;
};

const hasSessionTokenInUrl = (value = "") => {
  if (!value) {
    return false;
  }

  try {
    const parsedUrl = new URL(value, "https://cards.ofc.link");

    return sessionTokenQueryParams.some((param) =>
      parsedUrl.searchParams.has(param),
    );
  } catch {
    return sessionTokenQueryParams.some((param) => value.includes(`${param}=`));
  }
};

const getSignupManageUrl = (data = {}, email = "") => {
  const providedUrl = data.manageUrl || data.managementUrl || "";

  if (providedUrl && !hasSessionTokenInUrl(providedUrl)) {
    return providedUrl;
  }

  return buildManageCardUrl({ email, requireCode: true });
};

const buildManageCardCodeUrl = ({ email = "", code = "" } = {}) => {
  const params = new URLSearchParams();

  if (email) {
    params.set("email", email.trim());
  }

  if (code) {
    params.set("code", code.trim());
  }

  const queryString = params.toString();

  return `/gestisci${queryString ? `?${queryString}` : ""}`;
};

const getResponseSessionToken = (data = {}) =>
  data.token || data.sessionToken || data.magicToken || data.accessToken || "";

const trackHomeEvent = (name, props = {}) => {
  trackEvent(name, {
    category: "HomePage",
    ...props,
  });
};

const trackSignupEvent = (name, props = {}) => {
  trackEvent(name, {
    category: "Signup",
    ...props,
  });
};

const trackSelfServiceEvent = (name, props = {}) => {
  trackEvent(name, {
    category: "SelfService",
    ...props,
  });
};

const normalizeAnalyticsContext = (data = {}) => {
  const analytics = data.analytics || {};
  const card = data.card || {};
  const planMeta = getCardPlanMeta(card);
  const groupId =
    analytics.groupId ||
    analytics.companyId ||
    card.groupId ||
    card.companyId ||
    card.ownerId ||
    "";
  const cardId =
    analytics.cardId || card.cardId || card.objectId || card.id || "";

  return {
    profileId: analytics.profileId || analytics.userId || "",
    firstName: analytics.firstName || card.firstName || "",
    lastName: analytics.lastName || card.lastName || "",
    email: analytics.email || data.email || card.email || "",
    groupId,
    groupType: analytics.groupType || "company",
    groupName:
      analytics.groupName || analytics.companyName || card.companyName || "",
    cardId,
    cardSlug: analytics.cardSlug || card.slug || "",
    plan: analytics.plan || planMeta.plan || "none",
    planStatus: analytics.planStatus || planMeta.planStatus || "",
  };
};

const getAnalyticsEventProps = (analyticsContext = {}) => ({
  ...(analyticsContext.cardId ? { cardId: analyticsContext.cardId } : {}),
  ...(analyticsContext.groupId ? { groupId: analyticsContext.groupId } : {}),
  ...(analyticsContext.cardSlug ? { cardSlug: analyticsContext.cardSlug } : {}),
  plan: analyticsContext.plan || "none",
  ...(analyticsContext.planStatus
    ? { planStatus: analyticsContext.planStatus }
    : {}),
});

const identifyAnalyticsContext = (analyticsContext = {}) => {
  if (analyticsContext.profileId) {
    identifyUser({
      profileId: analyticsContext.profileId,
      firstName: analyticsContext.firstName,
      lastName: analyticsContext.lastName,
      email: analyticsContext.email,
    });
  }

  if (analyticsContext.groupId) {
    upsertGroup({
      id: analyticsContext.groupId,
      type: analyticsContext.groupType || "company",
      name: analyticsContext.groupName,
    });
    setGroup(analyticsContext.groupId);
  }
};

const getFileSizeBucket = (size = 0) => {
  if (size <= 100 * 1024) return "0-100kb";
  if (size <= 1024 * 1024) return "100kb-1mb";
  if (size <= 3 * 1024 * 1024) return "1mb-3mb";
  if (size <= 5 * 1024 * 1024) return "3mb-5mb";
  return "5mb+";
};

const maskEmail = (value) => {
  const [localPart, domain] = value.trim().split("@");

  if (!localPart || !domain) {
    return value;
  }

  const maskPart = (part) => {
    if (part.length <= 2) {
      return `${part[0] || ""}*`;
    }

    return `${part[0]}${"*".repeat(Math.min(part.length - 2, 12))}${
      part[part.length - 1]
    }`;
  };

  const [domainName, ...domainSuffix] = domain.split(".");

  return `${maskPart(localPart)}@${maskPart(domainName)}${
    domainSuffix.length ? `.${domainSuffix.join(".")}` : ""
  }`;
};

const normalizeEmailValue = (value = "") => value.trim().toLowerCase();

const getImageUploadValidationError = (file) => {
  if (!file) {
    return "Seleziona un file immagine.";
  }

  if (!imageContentTypes.includes(file.type)) {
    return "Formato non supportato. Usa JPG, PNG o WebP.";
  }

  if (file.size > maxImageUploadSize) {
    return "Immagine troppo grande. Il limite è 5 MB.";
  }

  return "";
};

const getSignedUploadHeaderNames = (uploadUrl) => {
  try {
    const signedHeaders =
      new URL(uploadUrl).searchParams.get("X-Amz-SignedHeaders") || "";

    return new Set(
      signedHeaders
        .split(";")
        .map((headerName) => headerName.trim().toLowerCase())
        .filter(Boolean),
    );
  } catch {
    return new Set();
  }
};

const shouldSendUploadHeader = ({ name, signedHeaderNames }) => {
  const normalizedName = name.toLowerCase();

  return (
    !normalizedName.startsWith("x-amz-") ||
    signedHeaderNames.has(normalizedName)
  );
};

const uploadFileToPresignedUrl = ({ file, upload, onProgress }) =>
  new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    const signedHeaderNames = getSignedUploadHeaderNames(upload.uploadUrl);

    request.open(upload.method || "PUT", upload.uploadUrl);
    Object.entries(upload.headers || {}).forEach(([name, value]) => {
      if (shouldSendUploadHeader({ name, signedHeaderNames })) {
        request.setRequestHeader(name, value);
      }
    });

    request.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve();
        return;
      }

      reject(new Error("Upload immagine non riuscito."));
    };

    request.onerror = () => {
      reject(new Error("Upload immagine non riuscito."));
    };

    request.send(file);
  });

const fieldGroups = [
  {
    title: "Dati personali",
    fields: [
      ["firstName", "Nome"],
      ["lastName", "Cognome"],
      ["role", "Ruolo"],
      ["email", "Email"],
      ["mobile", "Cellulare", "tel", "", "mobilePrefix"],
      [
        "avatarUrl",
        "Avatar / foto",
        "url",
        "Carica JPG, PNG o WebP. Consigliato formato quadrato.",
      ],
    ],
  },
  {
    title: "Dati aziendali",
    fields: [
      ["companyName", "Azienda"],
      ["address", "Indirizzo"],
      ["city", "Città"],
      ["zipCode", "CAP"],
      ["website", "Sito web", "url"],
      ["companyEmail", "Email ufficio", "email"],
      ["phone", "Telefono ufficio", "tel", "", "phonePrefix"],
      [
        "logoUrl",
        "Logo azienda",
        "url",
        "Carica JPG, PNG o WebP. Consigliato formato orizzontale.",
      ],
      [
        "description",
        "Descrizione",
        "textarea",
        "Massimo 240 caratteri. Usa un testo breve, 3 o 4 righe al massimo.",
      ],
      ["linkedin", "LinkedIn"],
      ["facebook", "Facebook"],
      ["instagram", "Instagram"],
      ["twitter", "X / Twitter"],
      ["youtube", "YouTube"],
      ["tiktok", "TikTok"],
    ],
  },
  {
    title: "Dati di fatturazione",
    fields: [
      ["billingCompanyName", "Ragione sociale"],
      ["vat", "Partita IVA"],
      ["electronicInvoicingId", "Codice SDI"],
      ["pec", "PEC"],
      ["billingAddress", "Indirizzo fatturazione"],
      ["billingCity", "Città fatturazione"],
      ["billingZipCode", "CAP fatturazione"],
    ],
  },
];

const socialFieldDefinitions = [
  ["linkedin", "LinkedIn", "mdi:linkedin"],
  ["facebook", "Facebook", "mdi:facebook"],
  ["instagram", "Instagram", "mdi:instagram"],
  ["twitter", "X / Twitter", "mdi:twitter"],
  ["youtube", "YouTube", "mdi:youtube"],
  ["tiktok", "TikTok", "mdi:music-note"],
];

const socialFieldNames = new Set(socialFieldDefinitions.map(([name]) => name));
const socialFieldLabels = socialFieldDefinitions.reduce(
  (acc, [name, label]) => ({
    ...acc,
    [name]: label,
  }),
  {},
);
const socialFieldAllowedDomains = {
  linkedin: ["linkedin.com"],
  facebook: ["facebook.com", "fb.com"],
  instagram: ["instagram.com"],
  twitter: ["x.com", "twitter.com"],
  youtube: ["youtube.com", "youtu.be"],
  tiktok: ["tiktok.com"],
};
const socialFieldDomainHints = Object.fromEntries(
  Object.entries(socialFieldAllowedDomains).map(([fieldName, domains]) => [
    fieldName,
    domains.join(" o "),
  ]),
);

const sectionIcons = {
  "Dati personali": "mdi:account-circle-outline",
  "Dati aziendali": "mdi:office-building-marker-outline",
  "Dati di fatturazione": "mdi:file-document-edit-outline",
};

const scrollToPageTop = () => {
  if (typeof window === "undefined" || typeof window.scrollTo !== "function") {
    return;
  }

  window.setTimeout(() => {
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      window.scrollTo(0, 0);
    }
  }, 0);
};

const phonePrefixOptions = [
  ["+39", "IT +39"],
  ["+33", "FR +33"],
  ["+34", "ES +34"],
  ["+49", "DE +49"],
  ["+43", "AT +43"],
  ["+41", "CH +41"],
  ["+32", "BE +32"],
  ["+31", "NL +31"],
  ["+351", "PT +351"],
  ["+44", "UK +44"],
  ["+353", "IE +353"],
  ["+45", "DK +45"],
  ["+46", "SE +46"],
  ["+47", "NO +47"],
  ["+358", "FI +358"],
  ["+48", "PL +48"],
  ["+420", "CZ +420"],
  ["+30", "GR +30"],
  ["+40", "RO +40"],
];

const featureCards = [
  {
    icon: "mdi:cellphone-link",
    title: "Più awareness e presenza",
    text: "Ogni condivisione del tuo biglietto digitale porta il tuo nome, il tuo ruolo e il brand direttamente nello smartphone di chi lo riceve.",
  },
  {
    icon: "mdi:flash",
    title: "Condivisione immediata",
    text: "Basta inviare un link o mostrare un QR code. Nessun biglietto cartaceo da ristampare, nessun dato da riscrivere manualmente.",
  },
  {
    icon: "mdi:qrcode",
    title: "QR scaricabile",
    text: "Dall'area profilo puoi generare e scaricare il QR code collegato alla tua card, pronto per materiali stampati, messaggi e condivisioni veloci.",
  },
  {
    icon: "mdi:email-edit-outline",
    title: "Firma email Plus",
    text: "Con Cards Plus trasformi i dati della card in una firma HTML pronta per Gmail, Outlook e Apple Mail, con template, colore, campi visibili e QR code opzionale.",
  },
  {
    icon: "mdi:creation-outline",
    title: "Avatar AI Plus *",
    text: "Genera o migliora l'immagine profilo con AI direttamente dall'area card. Funzione Plus disponibile tramite crediti.",
  },
  {
    icon: "mdi:palette-outline",
    title: "Look & feel Plus",
    text: "Personalizza palette, font e angoli della card pubblica partendo dal default OFC.",
  },
  {
    icon: "mdi:check-decagram",
    title: "Dati sempre aggiornati",
    text: "Se cambia email, numero o sito, aggiorni la card e da quel momento ogni condivisione contiene le informazioni corrette.",
  },
];

const audienceCards = [
  {
    icon: "mdi:office-building-marker",
    title: "Agenzie funebri e case funerarie",
    items: [
      "Contatti facilmente rintracciabili anche dopo il primo incontro.",
      "Brand, informazioni aziendali, indirizzo e mappa sempre presenti nella card.",
    ],
  },
  {
    icon: "mdi:handshake",
    title: "Fornitori e partner di filiera",
    items: [
      "Profilo professionale sempre pronto per clienti e collaboratori.",
      "Dati utili come IVA, PEC, indirizzo e link aziendali sempre ordinati.",
    ],
  },
  {
    icon: "mdi:briefcase-check",
    title: "Per chi lavora molto in mobilità",
    items: [
      "Hai sempre con te numero, email, sede, indirizzi e link utili.",
      "Riduci errori di trascrizione e semplifichi i passaggi operativi.",
    ],
  },
  {
    icon: "mdi:tools",
    title: "Uno strumento semplice da adottare",
    items: [
      "La pagina gratuita permette di iniziare subito.",
      "Il Plus sblocca strumenti più completi per chi usa la card ogni giorno.",
    ],
  },
];

const comparisonRows = [
  ["Prezzo", "0€ / gratis", "49€ / anno"],
  ["Cellulare, email, WhatsApp", true, true],
  ["Nome impresa", true, true],
  ["Pagina card condivisibile", true, true],
  ["Contatti aziendali", false, true],
  ["Indirizzo e mappa", false, true],
  ["QR code nella card pubblica", false, true],
  ["QR code scaricabile", true, true],
  ["Generatore firma email HTML", false, true],
  ["Generatore avatar con AI *", false, true],
  ["Personalizzazione look & feel", false, true],
  ["Dati utili per operatività / fatturazione", false, true],
  ["Widget dedicati", false, "coming"],
];

const faqs = [
  [
    "A chi è dedicata Cards?",
    "Cards è dedicata esclusivamente a persone, imprese e operatori della filiera del settore funebre: agenzie funebri, fornitori, consulenti e partner specializzati.",
  ],
  [
    "Se non sono del settore funebre posso comunque aprire un account?",
    "Il servizio è destinato al settore funebre. Le card possono includere riferimenti, testi e funzionalità pensati per quel contesto, quindi non sono adatte a professionisti o attività fuori settore.",
  ],
  [
    "Posso partire gratis e passare dopo al Plus?",
    "Sì. Il primo passo è sempre l'apertura del profilo gratuito. Una volta dentro il profilo, puoi decidere se acquistare il piano Plus.",
  ],
  [
    "A cosa serve il coupon?",
    "Il coupon permette di attivare condizioni dedicate quando disponibili.",
  ],
  [
    "Si possono aggiungere nuove funzioni in futuro?",
    "Sì. La struttura è pronta per nuovi widget e dati aggiuntivi senza cambiare il link della card.",
  ],
];

const signupFields = [
  ["signup-first-name", "firstName", "Nome", "text", "Mario"],
  ["signup-last-name", "lastName", "Cognome", "text", "Rossi"],
  [
    "signup-company-name",
    "companyName",
    "Nome impresa",
    "text",
    "Impresa Rossi",
  ],
  ["signup-email", "email", "Email", "email", "mario.rossi@email.it"],
  ["signup-phone", "phone", "Telefono", "tel", "+39 333 1234567"],
];

const signupLoadingPhrases = [
  "Sto verificando l'email inserita...",
  "Controllo se esiste già una Cards attiva...",
  "Preparo il profilo e la mail di accesso...",
  "Ci siamo quasi, sto finalizzando la richiesta...",
];

const emptyForm = fieldGroups
  .flatMap((group) => group.fields)
  .reduce((acc, [name, , , , prefixFieldName]) => {
    acc[name] = "";
    if (prefixFieldName) {
      acc[prefixFieldName] = "+39";
    }
    return acc;
  }, buildDefaultCardThemeFields());
const requiredCardFields = new Set([
  "firstName",
  "lastName",
  "companyName",
  "email",
]);
const freePlanEditableFields = new Set([
  "firstName",
  "lastName",
  "companyName",
  "email",
  "mobile",
  "avatarUrl",
]);
const cardFieldLabels = fieldGroups
  .flatMap((group) => group.fields)
  .reduce(
    (acc, [name, label, , , prefixFieldName]) => {
      acc[name] = label;
      if (prefixFieldName) {
        acc[prefixFieldName] = `Prefisso ${label}`;
      }
      return acc;
    },
    {
      themePreset: "Palette",
      themePrimary: "Colore principale",
      themeSecondary: "Colore secondario",
      themeAccent: "Colore accento",
      themeBackground: "Sfondo",
      themeSurface: "Superficie card",
      themeText: "Testo",
      themeFont: "Font",
      themeRadius: "Stile angoli",
    },
  );
const urlCardFields = new Set(
  fieldGroups
    .flatMap((group) => group.fields)
    .filter(([, , type]) => type === "url")
    .map(([name]) => name),
);
const fieldMaxLengths = {
  description: 240,
};
const additionalUrlCardFields = [
  "linkedin",
  "facebook",
  "instagram",
  "twitter",
  "youtube",
  "tiktok",
  "videoUrl",
];
const normalizableUrlCardFields = new Set([
  ...urlCardFields,
  ...additionalUrlCardFields,
]);
const recentlyAddedCardFields = [
  "role",
  "mobilePrefix",
  "phonePrefix",
  "companyEmail",
  "zipCode",
  "avatarUrl",
  "logoUrl",
  "youtube",
  "tiktok",
  "billingCompanyName",
  "billingAddress",
  "billingCity",
  "billingZipCode",
  "themePreset",
  "themePrimary",
  "themeSecondary",
  "themeAccent",
  "themeBackground",
  "themeSurface",
  "themeText",
  "themeFont",
  "themeRadius",
];

const getCardPlanMeta = (card = {}) => ({
  plan: card.plan || "",
  planStatus: card.planStatus || "",
  couponCode: card.couponCode || "",
  couponSponsorMode: card.couponSponsorMode || "",
  isDisabled: Boolean(card.isDisabled),
});

const normalizeUrlValue = (value = "") => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return "";
  }

  if (/^[a-z][a-z\d+.-]*:\/\//i.test(trimmedValue)) {
    return trimmedValue;
  }

  if (trimmedValue.startsWith("//")) {
    return `https:${trimmedValue}`;
  }

  return `https://${trimmedValue}`;
};

const isValidUrlValue = (value) => {
  const normalizedValue = normalizeUrlValue(value);

  if (!normalizedValue) {
    return true;
  }

  try {
    const url = new URL(normalizedValue);
    return (
      ["http:", "https:"].includes(url.protocol) &&
      (url.hostname.includes(".") || url.hostname === "localhost")
    );
  } catch {
    return false;
  }
};

const getUrlHostname = (value) => {
  const normalizedValue = normalizeUrlValue(value);

  if (!normalizedValue) {
    return "";
  }

  try {
    return new URL(normalizedValue).hostname.toLowerCase();
  } catch {
    return "";
  }
};

const isAllowedSocialDomain = (fieldName, value) => {
  const allowedDomains = socialFieldAllowedDomains[fieldName];

  if (!allowedDomains || !value?.trim()) {
    return true;
  }

  const hostname = getUrlHostname(value).replace(/^www\./, "");

  if (!hostname) {
    return false;
  }

  return allowedDomains.some(
    (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
  );
};

const normalizeCardUrlFields = (card) =>
  Object.entries(card).reduce((acc, [fieldName, value]) => {
    if (
      normalizableUrlCardFields.has(fieldName) &&
      typeof value === "string" &&
      value.trim()
    ) {
      acc[fieldName] = normalizeUrlValue(value);
      return acc;
    }

    acc[fieldName] = value;
    return acc;
  }, {});

const normalizeApiErrorDetails = (details) => {
  if (!details) {
    return [];
  }

  if (Array.isArray(details)) {
    return details
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }

        const fieldPath = Array.isArray(item.path)
          ? item.path[item.path.length - 1]
          : item.path;
        const fieldName =
          item.field ||
          fieldPath ||
          item.param ||
          item.name ||
          item.context?.key;
        const label = cardFieldLabels[fieldName] || fieldName;
        const message = item.message || item.error || item.reason;

        return [label, message].filter(Boolean).join(": ");
      })
      .filter(Boolean);
  }

  if (typeof details === "object") {
    return Object.entries(details)
      .map(([fieldName, value]) => {
        const label = cardFieldLabels[fieldName] || fieldName;
        const message = Array.isArray(value) ? value.join(", ") : value;

        return [label, message].filter(Boolean).join(": ");
      })
      .filter(Boolean);
  }

  return [String(details)];
};

const getCardApiErrorMessage = (data, fallback, payload = {}) => {
  const details = [
    ...normalizeApiErrorDetails(data?.errors),
    ...normalizeApiErrorDetails(data?.details),
    ...normalizeApiErrorDetails(data?.issues),
    ...normalizeApiErrorDetails(data?.error?.details),
  ];

  if (details.length) {
    return `${data?.message || fallback} ${details.join(" ")}`;
  }

  if (data?.code) {
    return data?.message || fallback;
  }

  const suspectFields = recentlyAddedCardFields.filter((fieldName) =>
    payload[fieldName]?.trim?.(),
  );

  if (suspectFields.length) {
    const suspectFieldLabels = suspectFields
      .map((fieldName) => cardFieldLabels[fieldName] || fieldName)
      .join(", ");

    return `${
      data?.message || fallback
    }\n\nIl server non ha indicato quale campo non è valido. Controlla che API e database accettino questi campi inviati dal frontend: ${suspectFieldLabels}.`;
  }

  if (data?.message) {
    return `${data.message}\n\nIl server non ha indicato quale campo non è valido.`;
  }

  return data?.message || fallback;
};

export function CardManager({
  initialEmail: providedInitialEmail = "",
  showPublicLink = true,
  compact = false,
  startWithMaskedEmail = false,
  onAuthenticatedViewChange,
} = {}) {
  const apiBaseUrl = useMemo(getApiBaseUrl, []);
  const urlInitialEmail = useMemo(
    () => getSearchParams().get("email") || "",
    [],
  );
  const urlSessionToken = useMemo(getUrlSessionToken, []);
  const urlLoginCode = useMemo(getUrlLoginCode, []);
  const urlWantsCodeEntry = useMemo(getUrlWantsCodeEntry, []);
  const initialSessionToken = useMemo(
    () =>
      urlSessionToken ||
      (urlLoginCode || urlWantsCodeEntry ? "" : getStoredSessionToken()),
    [urlLoginCode, urlSessionToken, urlWantsCodeEntry],
  );
  const initialEmail = providedInitialEmail || urlInitialEmail;
  const shouldStartWithMaskedEmail =
    startWithMaskedEmail && Boolean(initialEmail);
  const shouldStartWithCodeEntry =
    urlWantsCodeEntry && Boolean(initialEmail) && !urlLoginCode;
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState(urlLoginCode);
  const [token, setToken] = useState(initialSessionToken);
  const [form, setForm] = useState(emptyForm);
  const [cardPlanMeta, setCardPlanMeta] = useState(getCardPlanMeta());
  const [analyticsContext, setAnalyticsContext] = useState({});
  const [currentSavedCard, setCurrentSavedCard] = useState(emptyForm);
  const [cardUrl, setCardUrl] = useState("");
  const [cardSlug, setCardSlug] = useState("");
  const [currentCardEmail, setCurrentCardEmail] = useState("");
  const [step, setStep] = useState(
    token || (urlLoginCode && initialEmail)
      ? "checking"
      : shouldStartWithCodeEntry
        ? "code"
        : shouldStartWithMaskedEmail
          ? "confirmEmail"
          : "email",
  );
  const [hasCard, setHasCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [smsFallbackOpen, setSmsFallbackOpen] = useState(false);
  const [smsPhone, setSmsPhone] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [smsLoading, setSmsLoading] = useState(false);
  const [cardValidationErrors, setCardValidationErrors] = useState({});
  const [uploadState, setUploadState] = useState({});
  const [addedSocialFields, setAddedSocialFields] = useState([]);
  const [selectedSocialField, setSelectedSocialField] = useState("");
  const [isAvatarAiModalOpen, setIsAvatarAiModalOpen] = useState(false);
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false);

  const applyCardResponse = useCallback((data, nextStep = "overview") => {
    const responseToken = getResponseSessionToken(data);
    const nextAnalyticsContext = normalizeAnalyticsContext(data);

    if (responseToken) {
      setToken(responseToken);
      storeSessionToken(responseToken);
    }

    if (data.card) {
      setHasCard(true);
      setCardSlug(data.card.slug || "");
      setCurrentCardEmail(data.card.email || "");
      setCardPlanMeta(getCardPlanMeta(data.card));
      setAddedSocialFields([]);
      setSelectedSocialField("");
      setForm({
        ...emptyForm,
        ...Object.fromEntries(
          Object.keys(emptyForm).map((key) => [
            key,
            data.card[key] || emptyForm[key],
          ]),
        ),
      });
      setCurrentSavedCard({
        ...data.card,
        cardUrl: data.url || data.card.cardUrl || data.card.url || "",
      });
    } else {
      setHasCard(false);
      setCardSlug("");
      setCurrentCardEmail("");
      setCardPlanMeta(getCardPlanMeta());
      setAddedSocialFields([]);
      setSelectedSocialField("");
      setForm({
        ...emptyForm,
        email: data.email || "",
      });
      setCurrentSavedCard(emptyForm);
    }
    setAnalyticsContext(nextAnalyticsContext);
    identifyAnalyticsContext(nextAnalyticsContext);
    if (nextAnalyticsContext.profileId || nextAnalyticsContext.groupId) {
      trackSelfServiceEvent("self_service_identified", {
        ...getAnalyticsEventProps(nextAnalyticsContext),
        hasCard: Boolean(data.card || data.hasCard),
      });
    }
    setCardValidationErrors({});
    setUploadState({});
    setAddedSocialFields([]);
    setSelectedSocialField("");

    setCardUrl(data.url || "");

    setStep(
      data.card?.isDisabled && nextStep === "edit" ? "overview" : nextStep,
    );
  }, []);

  const resetExpiredSession = useCallback(() => {
    clearStoredSessionToken();
    clearIdentity();
    setToken("");
    setStep(shouldStartWithMaskedEmail ? "confirmEmail" : "email");
    setForm(emptyForm);
    setCardPlanMeta(getCardPlanMeta());
    setAnalyticsContext({});
    setCurrentSavedCard(emptyForm);
    setCardUrl("");
    setCardSlug("");
    setCurrentCardEmail("");
    setCode("");
    setHasCard(null);
    setCardValidationErrors({});
    setUploadState({});
    setAddedSocialFields([]);
    setSelectedSocialField("");
    setMessage("");
    setError("Sessione scaduta. Richiedi un nuovo codice OTP per continuare.");
    setSmsFallbackOpen(false);
    setSmsPhone("");
    setSmsLoading(false);
  }, [shouldStartWithMaskedEmail]);

  const buildCardPayload = () => {
    const basePayload = {
      ...form,
      mobilePrefix: form.mobile?.trim() ? form.mobilePrefix || "+39" : "",
      phonePrefix: form.phone?.trim() ? form.phonePrefix || "+39" : "",
    };

    if (hasFreePlan) {
      const fallbackCompanyName = `${form.firstName || ""} ${
        form.lastName || ""
      }`.trim();

      return normalizeCardUrlFields({
        firstName: basePayload.firstName,
        lastName: basePayload.lastName,
        email: basePayload.email,
        mobile: basePayload.mobile,
        mobilePrefix: basePayload.mobilePrefix,
        avatarUrl: basePayload.avatarUrl,
        companyName: basePayload.companyName || fallbackCompanyName,
      });
    }

    return normalizeCardUrlFields(basePayload);
  };

  const validateCardForm = () => {
    const nextErrors = {};

    const activeRequiredCardFields = requiredCardFields;
    const activeUrlCardFields = hasFreePlan
      ? new Set(["avatarUrl"])
      : normalizableUrlCardFields;

    activeRequiredCardFields.forEach((fieldName) => {
      if (!form[fieldName]?.trim()) {
        nextErrors[fieldName] = "Campo obbligatorio.";
      }
    });

    activeUrlCardFields.forEach((fieldName) => {
      if (!isValidUrlValue(form[fieldName] || "")) {
        nextErrors[fieldName] = "Inserisci un URL valido, es. www.sito.it.";
        return;
      }

      if (!isAllowedSocialDomain(fieldName, form[fieldName] || "")) {
        const socialLabel = socialFieldLabels[fieldName] || "social";
        const domainHint = socialFieldDomainHints[fieldName];
        nextErrors[fieldName] = domainHint
          ? `Inserisci un link ${socialLabel} valido (${domainHint}).`
          : `Inserisci un link ${socialLabel} valido.`;
      }
    });

    if (hasPlusPlan) {
      [
        "themePrimary",
        "themeSecondary",
        "themeAccent",
        "themeBackground",
        "themeSurface",
        "themeText",
      ].forEach((fieldName) => {
        if (!isValidHexColor(form[fieldName] || "")) {
          nextErrors[fieldName] = "Inserisci un colore HEX valido.";
        }
      });
    }

    setCardValidationErrors(nextErrors);

    if (!Object.keys(nextErrors).length) {
      return true;
    }

    const fieldList = Object.keys(nextErrors)
      .map((fieldName) => cardFieldLabels[fieldName] || fieldName)
      .join(", ");

    setError(`Controlla i campi evidenziati: ${fieldList}.`);
    return false;
  };

  const hasCompanyAddressData = Boolean(
    form.address?.trim() || form.city?.trim() || form.zipCode?.trim(),
  );

  const copyCompanyAddressToBilling = () => {
    setForm((current) => ({
      ...current,
      billingAddress: current.address,
      billingCity: current.city,
      billingZipCode: current.zipCode,
    }));
  };

  const [isVatLookupOpen, setIsVatLookupOpen] = useState(false);

  const applyVatLookupResult = (data) => {
    setForm((current) => ({
      ...current,
      companyName: data.ragioneSociale || current.companyName,
      address: data.via || current.address,
      city: data.citta || current.city,
      zipCode: data.cap || current.zipCode,
      billingCompanyName: data.ragioneSociale || current.billingCompanyName,
      billingAddress: data.via || current.billingAddress,
      billingCity: data.citta || current.billingCity,
      billingZipCode: data.cap || current.billingZipCode,
      vat: data.vat || current.vat,
      electronicInvoicingId:
        data.codiceUnivocoSdi || current.electronicInvoicingId,
      pec: data.pec || current.pec,
    }));
  };

  const clearCardFieldValidationError = (fieldName) => {
    setCardValidationErrors((current) => {
      const next = { ...current };
      delete next[fieldName];
      return next;
    });
  };

  const updateCardField = (fieldName, value) => {
    setForm((current) => ({
      ...current,
      [fieldName]: value,
    }));
    clearCardFieldValidationError(fieldName);
  };

  const visibleSocialFields = socialFieldDefinitions.filter(
    ([name]) => form[name]?.trim() || addedSocialFields.includes(name),
  );
  const availableSocialFields = socialFieldDefinitions.filter(
    ([name]) => !form[name]?.trim() && !addedSocialFields.includes(name),
  );

  const addSocialField = () => {
    if (!selectedSocialField) {
      return;
    }

    setAddedSocialFields((current) =>
      current.includes(selectedSocialField)
        ? current
        : [...current, selectedSocialField],
    );
    setSelectedSocialField("");
  };

  const removeSocialField = (fieldName) => {
    updateCardField(fieldName, "");
    setAddedSocialFields((current) =>
      current.filter((name) => name !== fieldName),
    );
  };

  const updateThemePreset = (presetId) => {
    setForm((current) => ({
      ...current,
      ...buildCardThemeFieldsFromPreset(presetId),
    }));
    [
      "themePreset",
      "themePrimary",
      "themeSecondary",
      "themeAccent",
      "themeBackground",
      "themeSurface",
      "themeText",
    ].forEach(clearCardFieldValidationError);
  };

  const updateThemeField = (fieldName, value) => {
    setForm((current) => ({
      ...current,
      themePreset:
        fieldName.startsWith("theme") &&
        fieldName !== "themePreset" &&
        fieldName !== "themeFont" &&
        fieldName !== "themeRadius"
          ? "custom"
          : current.themePreset,
      [fieldName]: value,
    }));
    clearCardFieldValidationError(fieldName);
  };

  const requestImageUploadUrl = async ({ fieldName, file }) => {
    const response = await fetch(`${apiBaseUrl}/self/upload-url`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        field: fieldName,
        fileName: file.name,
        contentType: file.type,
        size: file.size,
        slug: cardSlug,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        trackSelfServiceEvent("self_service_session_expired", {
          action: "upload_image",
        });
        resetExpiredSession();
      }

      throw new Error(data.message || "Impossibile preparare l'upload.");
    }

    return data;
  };

  const uploadCardImage = async ({ fieldName, file }) => {
    const validationError = getImageUploadValidationError(file);

    setMessage("");
    setError("");

    if (validationError) {
      setError(validationError);
      setUploadState((current) => ({
        ...current,
        [fieldName]: { status: "error", progress: 0 },
      }));
      trackSelfServiceEvent("self_service_image_upload_rejected", {
        ...getAnalyticsEventProps(analyticsContext),
        field: fieldName,
        fileType: file.type || "unknown",
        fileSizeBucket: getFileSizeBucket(file.size || 0),
        source: "manual_upload",
      });
      return;
    }

    if (!apiBaseUrl || !token) {
      setError("Sessione non valida. Richiedi un nuovo codice OTP.");
      return;
    }

    setUploadState((current) => ({
      ...current,
      [fieldName]: {
        status: "uploading",
        progress: 0,
        fileName: file.name,
      },
    }));

    try {
      const upload = await requestImageUploadUrl({ fieldName, file });

      await uploadFileToPresignedUrl({
        file,
        upload,
        onProgress: (progress) => {
          setUploadState((current) => ({
            ...current,
            [fieldName]: {
              ...current[fieldName],
              status: "uploading",
              progress,
            },
          }));
        },
      });

      updateCardField(fieldName, upload.assetUrl);
      setUploadState((current) => ({
        ...current,
        [fieldName]: {
          status: "done",
          progress: 100,
          fileName: file.name,
        },
      }));
      setMessage(
        "Immagine caricata. Salva la card per pubblicare la modifica.",
      );
      trackSelfServiceEvent("self_service_image_uploaded", {
        ...getAnalyticsEventProps(analyticsContext),
        field: fieldName,
        fileType: file.type || "unknown",
        fileSizeBucket: getFileSizeBucket(file.size || 0),
        source: "manual_upload",
      });
    } catch (err) {
      setUploadState((current) => ({
        ...current,
        [fieldName]: {
          ...current[fieldName],
          status: "error",
          progress: current[fieldName]?.progress || 0,
        },
      }));
      trackSelfServiceEvent("self_service_image_upload_failed", {
        ...getAnalyticsEventProps(analyticsContext),
        field: fieldName,
        fileType: file.type || "unknown",
        fileSizeBucket: getFileSizeBucket(file.size || 0),
        source: "manual_upload",
      });
      setError(err.message || "Upload immagine non riuscito.");
    }
  };

  useEffect(() => {
    if (!urlSessionToken) {
      return;
    }

    trackSelfServiceEvent("self_service_magic_link_received", {
      hasEmail: Boolean(initialEmail),
    });
    storeSessionToken(urlSessionToken);
    removeSessionTokenFromUrl();
    removeLoginCodeFromUrl();
  }, [urlSessionToken, initialEmail]);

  useEffect(() => {
    if (!urlLoginCode || urlSessionToken || !apiBaseUrl) {
      return;
    }

    removeLoginCodeFromUrl();

    if (!initialEmail) {
      trackSelfServiceEvent("self_service_otp_link_failed", {
        reason: "missing_email",
      });
      setStep("email");
      setError(
        "Link incompleto. Inserisci l'email e richiedi un nuovo codice.",
      );
      return;
    }

    let ignore = false;

    async function verifyCodeFromUrl() {
      setLoading(true);
      setError("");
      setMessage("Verifica link in corso...");

      try {
        const response = await fetch(`${apiBaseUrl}/self/verify-code`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: initialEmail, code: urlLoginCode }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || "Link non valido o scaduto.");
        }

        if (!ignore) {
          applyCardResponse(
            data,
            data.card || data.hasCard ? "edit" : "create",
          );
          trackSelfServiceEvent("self_service_otp_link_verified", {
            hasCard: Boolean(data.card || data.hasCard),
          });
          setMessage("Accesso effettuato.");
        }
      } catch (err) {
        if (!ignore) {
          clearStoredSessionToken();
          setToken("");
          setStep("code");
          setCode(urlLoginCode);
          trackSelfServiceEvent("self_service_otp_link_failed");
          setError(
            err.message ||
              "Link non valido o scaduto. Puoi inserire il codice o richiederne uno nuovo.",
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    trackSelfServiceEvent("self_service_otp_link_received", {
      hasEmail: Boolean(initialEmail),
    });
    verifyCodeFromUrl();

    return () => {
      ignore = true;
    };
  }, [
    apiBaseUrl,
    applyCardResponse,
    initialEmail,
    urlLoginCode,
    urlSessionToken,
  ]);

  useEffect(() => {
    if (!token || !apiBaseUrl) {
      return;
    }

    let ignore = false;

    async function loadCard() {
      try {
        const response = await fetch(`${apiBaseUrl}/self/card`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Sessione scaduta.");
        }

        const data = await response.json();

        if (!ignore) {
          trackSelfServiceEvent("self_service_session_loaded", {
            hasCard: Boolean(data.card || data.hasCard),
          });
          applyCardResponse(
            data,
            data.card || data.hasCard ? "edit" : "create",
          );
        }
      } catch {
        clearStoredSessionToken();
        if (!ignore) {
          trackSelfServiceEvent("self_service_session_failed");
          resetExpiredSession();
        }
      }
    }

    loadCard();

    return () => {
      ignore = true;
    };
  }, [token, apiBaseUrl, applyCardResponse, resetExpiredSession]);

  const submitEmail = (event) => {
    event.preventDefault();
    trackSelfServiceEvent("self_service_email_submitted", {
      source: shouldStartWithMaskedEmail ? "masked_email" : "email_form",
    });
    setError("");
    setMessage("");
    requestOtpCode();
  };

  const requestOtpCode = async () => {
    setLoading(true);
    setError("");
    setMessage("");
    let failureTracked = false;

    try {
      const response = await fetch(`${apiBaseUrl}/self/request-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        failureTracked = true;
        trackSelfServiceEvent("self_service_otp_request_failed", {
          statusCode: response.status,
        });
        throw new Error(data.message || "Impossibile inviare il codice.");
      }

      setStep("code");
      setSmsFallbackOpen(false);
      trackSelfServiceEvent("self_service_otp_requested", {
        debugCode: Boolean(data.debugCode),
      });
      if (data.debugCode) {
        setCode(data.debugCode);
        setMessage(
          `${data.message || "Codice generato."} Codice test: ${data.debugCode}`,
        );
      } else {
        setMessage(data.message || "Codice inviato via email.");
      }
    } catch (err) {
      if (!failureTracked) {
        trackSelfServiceEvent("self_service_otp_request_failed");
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const requestSmsOtpCode = async (event) => {
    event.preventDefault();

    if (!smsPhone.trim()) {
      setError("Inserisci il numero di cellulare associato alla Cards.");
      return;
    }

    setSmsLoading(true);
    setError("");
    setMessage("");
    let failureTracked = false;

    try {
      const response = await fetch(`${apiBaseUrl}/self/request-code-sms`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ phone: smsPhone.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        failureTracked = true;
        trackSelfServiceEvent("self_service_otp_sms_request_failed", {
          statusCode: response.status,
        });
        throw new Error(data.message || "Impossibile inviare il codice SMS.");
      }

      trackSelfServiceEvent("self_service_otp_sms_requested", {
        matchedCards: data.matchedCards || 0,
        smsCount: data.smsCount || 0,
      });

      const currentEmailDebugCode = data.debugCodes?.find(
        (entry) =>
          normalizeEmailValue(entry.email || "") === normalizeEmailValue(email),
      );

      if (currentEmailDebugCode?.code) {
        setCode(currentEmailDebugCode.code);
        setMessage(
          `${data.message || "Codice SMS generato."} Codice test: ${currentEmailDebugCode.code}`,
        );
      } else {
        setMessage(data.message || "Codice inviato via SMS.");
      }
    } catch (err) {
      if (!failureTracked) {
        trackSelfServiceEvent("self_service_otp_sms_request_failed");
      }
      setError(err.message);
    } finally {
      setSmsLoading(false);
    }
  };

  const submitCode = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");
    let failureTracked = false;

    try {
      const response = await fetch(`${apiBaseUrl}/self/verify-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, code }),
      });

      const data = await response.json();

      if (!response.ok) {
        failureTracked = true;
        trackSelfServiceEvent("self_service_otp_verify_failed", {
          statusCode: response.status,
        });
        throw new Error(data.message || "Codice non valido.");
      }

      applyCardResponse(data, data.card || data.hasCard ? "edit" : "create");
      trackSelfServiceEvent("self_service_otp_verified", {
        hasCard: Boolean(data.card || data.hasCard),
      });
      setMessage("Accesso effettuato.");
    } catch (err) {
      if (!failureTracked) {
        trackSelfServiceEvent("self_service_otp_verify_failed");
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateCard = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    if (!validateCardForm()) {
      scrollToPageTop();
      return;
    }
    setLoading(true);
    let failureTracked = false;

    try {
      const cardPayload = buildCardPayload();
      const nextEmail = normalizeEmailValue(cardPayload.email || "");
      const previousEmail = normalizeEmailValue(currentCardEmail);
      const isChangingPersonalEmail =
        previousEmail && nextEmail && nextEmail !== previousEmail;

      if (isChangingPersonalEmail) {
        const confirmed = window.confirm(
          "Stai modificando l'email personale della Cards. Per sicurezza l'email non verrà cambiata subito: invieremo un nuovo codice OTP alla nuova casella e dovrai verificarlo per completare la modifica. Vuoi continuare?",
        );

        if (!confirmed) {
          setMessage("");
          setError("");
          return;
        }
      }

      const response = await fetch(`${apiBaseUrl}/self/card`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ card: cardPayload }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          failureTracked = true;
          trackSelfServiceEvent("self_service_session_expired", {
            action: "update_card",
          });
          resetExpiredSession();
          return;
        }

        failureTracked = true;
        trackSelfServiceEvent("self_service_card_update_failed", {
          statusCode: response.status,
        });
        throw new Error(
          getCardApiErrorMessage(
            data,
            "Impossibile salvare la card.",
            cardPayload,
          ),
        );
      }

      const responseAnalyticsContext = normalizeAnalyticsContext(data);
      const nextSavedCard = {
        ...currentSavedCard,
        ...cardPayload,
        ...(data.card || {}),
        cardUrl: data.url || currentSavedCard.cardUrl || "",
      };
      const featureSnapshot = getCardFeatureSnapshot(nextSavedCard);
      const changedFeatures = getChangedFeatures(
        currentSavedCard,
        nextSavedCard,
      );

      applyCardResponse(data, "overview");
      trackSelfServiceEvent("self_service_card_updated", {
        ...getAnalyticsEventProps(responseAnalyticsContext),
        features_enabled: featureSnapshot.featuresEnabled,
        features_enabled_count: featureSnapshot.featuresEnabledCount,
        changed_fields: getChangedFields(currentSavedCard, nextSavedCard),
        changed_features: changedFeatures.changed,
        changed_features_enabled: changedFeatures.enabled,
        changed_features_disabled: changedFeatures.disabled,
      });
      if (data.emailChangeVerificationRequired) {
        setMessage(
          `Card aggiornata. Abbiamo inviato un codice OTP a ${data.pendingEmail}: verifica la nuova email per completare il cambio dell'email personale.`,
        );
      } else {
        setMessage("Card aggiornata.");
      }
      scrollToPageTop();
    } catch (err) {
      if (!failureTracked) {
        trackSelfServiceEvent("self_service_card_update_failed");
      }
      setError(err.message);
      scrollToPageTop();
    } finally {
      setLoading(false);
    }
  };

  const createCard = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    if (!validateCardForm()) {
      scrollToPageTop();
      return;
    }
    setLoading(true);
    let failureTracked = false;

    try {
      const cardPayload = buildCardPayload();
      const response = await fetch(`${apiBaseUrl}/self/card`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ card: cardPayload }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          failureTracked = true;
          trackSelfServiceEvent("self_service_session_expired", {
            action: "create_card",
          });
          resetExpiredSession();
          return;
        }

        if (response.status === 409 && data.card) {
          applyCardResponse(data, "overview");
          failureTracked = true;
          trackSelfServiceEvent("self_service_card_create_conflict");
          setError(
            data.message || "Esiste già una Cards associata a questa email.",
          );
          scrollToPageTop();
          return;
        }

        failureTracked = true;
        trackSelfServiceEvent("self_service_card_create_failed", {
          statusCode: response.status,
        });
        throw new Error(
          getCardApiErrorMessage(
            data,
            "Impossibile creare la card.",
            cardPayload,
          ),
        );
      }

      const responseAnalyticsContext = normalizeAnalyticsContext(data);
      const nextSavedCard = {
        ...cardPayload,
        ...(data.card || {}),
        cardUrl: data.url || "",
      };
      const featureSnapshot = getCardFeatureSnapshot(nextSavedCard);

      applyCardResponse(data, "overview");
      trackSelfServiceEvent("self_service_card_created", {
        ...getAnalyticsEventProps(responseAnalyticsContext),
        features_enabled: featureSnapshot.featuresEnabled,
        features_enabled_count: featureSnapshot.featuresEnabledCount,
      });
      setMessage("Card creata.");
      scrollToPageTop();
    } catch (err) {
      if (!failureTracked) {
        trackSelfServiceEvent("self_service_card_create_failed");
      }
      setError(err.message);
      scrollToPageTop();
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    trackSelfServiceEvent("self_service_logout", {
      ...getAnalyticsEventProps(analyticsContext),
    });
    clearStoredSessionToken();
    clearIdentity();
    setToken("");
    setEmail("");
    setStep("email");
    setForm(emptyForm);
    setCardPlanMeta(getCardPlanMeta());
    setAnalyticsContext({});
    setCurrentSavedCard(emptyForm);
    setCardUrl("");
    setCardSlug("");
    setCurrentCardEmail("");
    setCode("");
    setHasCard(null);
    setCardValidationErrors({});
    setUploadState({});
    setAddedSocialFields([]);
    setSelectedSocialField("");
    setMessage("");
    setError("");
  };

  const renderImageUploadField = ({ name, label, helpText }) => {
    const fieldError = cardValidationErrors[name];
    const inputBorderClass = fieldError
      ? "border-red-300 focus:border-red-500 focus:ring-red-100"
      : "border-slate-200 focus:border-cyan-600 focus:ring-cyan-100";
    const upload = uploadState[name] || {};
    const isUploading = upload.status === "uploading";
    const imageUrl = form[name]?.trim() || "";
    const settings = imageUploadSettings[name] || imageUploadSettings.avatarUrl;

    return (
      <div key={name} className="block">
        <div className="text-xs font-semibold text-slate-500">{label}</div>
        <div
          className={`mt-1 rounded-lg border bg-slate-50/70 p-3 transition ${
            fieldError ? "border-red-300" : "border-slate-200"
          }`}
        >
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div
              className={`flex shrink-0 items-center justify-center overflow-hidden bg-white ring-1 ring-slate-200 ${settings.previewClassName}`}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={label}
                  className={`h-full w-full ${settings.imageClassName}`}
                />
              ) : (
                <div
                  className={`flex h-full w-full items-center justify-center bg-cyan-50 text-[#1b8395] ${settings.emptyPreviewClassName}`}
                >
                  <Icon icon={settings.icon} className="text-3xl" />
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="grid gap-2 md:flex md:flex-wrap">
                {name === "avatarUrl" && cardPlanMeta.plan === "plus" ? (
                  <button
                    type="button"
                    onClick={() => setIsAvatarAiModalOpen(true)}
                    disabled={loading || !apiBaseUrl || !token}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-fuchsia-600 px-3 py-3 text-xs font-bold text-white transition hover:bg-fuchsia-700 md:py-2 disabled:opacity-50"
                  >
                    <Icon icon="mdi:auto-fix" className="text-base" />
                    Modifica avatar
                  </button>
                ) : (
                  <>
                    <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-md bg-[#1b8395] px-3 py-3 text-xs font-bold text-white transition hover:bg-[#13526f] md:py-2">
                      <Icon
                        icon="mdi:cloud-upload-outline"
                        className="text-base"
                      />
                      {isUploading ? "Carico..." : "Carica file"}
                      <input
                        type="file"
                        accept={imageContentTypes.join(",")}
                        disabled={
                          isUploading || loading || !apiBaseUrl || !token
                        }
                        aria-label={`Carica ${label}`}
                        className="sr-only"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          event.target.value = "";
                          uploadCardImage({ fieldName: name, file });
                        }}
                      />
                    </label>
                    {imageUrl && (
                      <button
                        type="button"
                        onClick={() => {
                          const confirmed = window.confirm(
                            `Sei sicuro di voler rimuovere ${label.toLowerCase()}?`,
                          );
                          if (!confirmed) return;
                          updateCardField(name, "");
                          setUploadState((current) => ({
                            ...current,
                            [name]: { status: "idle", progress: 0 },
                          }));
                        }}
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-3 text-xs font-bold text-slate-600 transition hover:bg-slate-50 md:py-2"
                      >
                        <Icon
                          icon="mdi:trash-can-outline"
                          className="text-base"
                        />
                        Rimuovi
                      </button>
                    )}
                  </>
                )}
              </div>
              {isUploading && (
                <div className="mt-3" aria-live="polite">
                  <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full bg-[#1b8395] transition-all"
                      style={{ width: `${upload.progress || 4}%` }}
                    />
                  </div>
                  <p className="mt-1 truncate text-[11px] font-semibold text-slate-500">
                    {upload.fileName || "Upload"} · {upload.progress || 0}%
                  </p>
                </div>
              )}
            </div>
          </div>

          <label
            htmlFor={`card-field-${name}`}
            className="mt-3 block text-[11px] font-semibold text-slate-400"
          >
            URL immagine
          </label>
          <input
            id={`card-field-${name}`}
            type="text"
            inputMode="url"
            value={form[name] || ""}
            onChange={(event) => updateCardField(name, event.target.value)}
            aria-invalid={Boolean(fieldError)}
            className={`mt-1 w-full rounded-md border bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:ring-2 md:py-2.5 ${inputBorderClass}`}
          />
        </div>
        {fieldError && (
          <span className="mt-1 block text-[11px] font-bold leading-4 text-red-600">
            {fieldError}
          </span>
        )}
        {helpText && (
          <span className="mt-1 block text-[11px] font-medium leading-4 text-slate-400">
            {helpText}
          </span>
        )}
      </div>
    );
  };

  const isAuthenticatedCardView = ["overview", "edit", "create"].includes(step);
  const isUploadingImage = Object.values(uploadState).some(
    (state) => state.status === "uploading",
  );
  const isPlusCouponPlan =
    cardPlanMeta.plan === "plus" &&
    cardPlanMeta.planStatus === "coupon_lifetime";
  const hasAnnunciFunebriCouponSponsor =
    cardPlanMeta.couponSponsorMode === "ofc_af" ||
    ["FIERA", "TANEXPO26", "OFC-TANEXPO26"].includes(
      normalizeCouponCode(cardPlanMeta.couponCode),
    );
  const couponSponsorCards = [
    {
      name: "Onoranzefunebricloud",
      logoUrl: ofcLogoUrl,
      description:
        "Soluzioni digitali e software per organizzare e valorizzare il lavoro delle imprese funebri.",
      link: "https://onoranzefunebricloud.com",
    },
    ...(hasAnnunciFunebriCouponSponsor
      ? [
          {
            name: "Annuncifunebri",
            logoUrl: annunciFunebriFairLogoUrl,
            description:
              "Necrologi online, visibilita digitale e servizi pensati per le case funerarie.",
            link: "https://annuncifunebri.it",
          },
        ]
      : []),
  ];
  const hasFreePlan = cardPlanMeta.plan === "gratis" || !cardPlanMeta.plan;
  const hasPlusPlan = cardPlanMeta.plan === "plus";
  const isCardDisabled = Boolean(cardPlanMeta.isDisabled);
  const managerFullName =
    `${form.firstName || ""} ${form.lastName || ""}`.trim();
  const activeFieldGroups = hasPlusPlan
    ? fieldGroups
    : fieldGroups
        .map((group) => ({
          ...group,
          fields: group.fields.filter(([name]) =>
            freePlanEditableFields.has(name),
          ),
        }))
        .filter((group) => group.fields.length > 0);
  const editableFieldGroups = activeFieldGroups
    .map((group) => ({
      ...group,
      fields: group.fields.filter(([name]) => !socialFieldNames.has(name)),
    }))
    .filter((group) => group.fields.length > 0);

  const renderPlusUpgradeNotice = () => {
    if (!hasFreePlan) {
      return null;
    }

    return (
      <div className="rounded-lg border border-amber-100 bg-amber-50 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.08em] text-amber-700">
              Funzioni Plus bloccate
            </p>
            <p className="mt-1 text-sm font-semibold leading-6 text-slate-700">
              Dati aziendali, logo, sito, social, fatturazione, QR code nella
              card, mappa, firma email HTML e look & feel personalizzato sono
              disponibili con Cards Plus.
            </p>
          </div>
          <a
            href="mailto:cards@onoranzefunebricloud.com?subject=Attivazione%20Cards%20Plus"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#1b8395] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#13526f]"
          >
            <Icon icon="mdi:email-fast-outline" className="text-lg" />
            Contattaci per Plus
          </a>
        </div>
      </div>
    );
  };

  const renderPlanNotice = () => {
    if (!hasCard) {
      return null;
    }

    if (isPlusCouponPlan) {
      return (
        <div className="overflow-hidden rounded-lg bg-gradient-to-br from-[#073f3e] via-[#0f5f5d] to-[#1b8395] p-4 text-white shadow-lg shadow-slate-950/15 ring-1 ring-white/10">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white/12 text-[#bfe3e4] ring-1 ring-white/15">
              <Icon icon="mdi:star-circle-outline" className="text-2xl" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold uppercase tracking-[0.08em] text-cyan-100">
                Piano Cards Plus
              </p>
              <h4 className="mt-1 text-base font-extrabold text-white">
                Cards Plus gratuito
              </h4>
              {cardPlanMeta.couponCode && (
                <p className="mt-3 text-xs font-semibold text-cyan-50/85">
                  Coupon applicato: {cardPlanMeta.couponCode}
                </p>
              )}
              <p className="mt-2 text-xs font-medium leading-5 text-cyan-50/80">
                Condizione speciale collegata al coupon: l'accesso a Cards Plus
                è offerto dagli sponsor associati al codice riscattato.
              </p>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {couponSponsorCards.map((sponsor) => (
                  <a
                    key={sponsor.name}
                    href={sponsor.link}
                    target="_blank"
                    rel="noreferrer"
                    className="min-w-0 rounded-lg bg-white p-3 text-[#073f35] shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={sponsor.logoUrl}
                        alt={sponsor.name}
                        className="h-9 max-w-[120px] object-contain"
                      />
                      <Icon
                        icon="mdi:open-in-new"
                        className="ml-auto shrink-0 text-lg text-[#1b8395]"
                      />
                    </div>
                    <p className="mt-2 text-xs font-bold">{sponsor.name}</p>
                    <p className="mt-1 text-xs font-medium leading-5 text-slate-600">
                      {sponsor.description}
                    </p>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (hasFreePlan) {
      return (
        <div className="rounded-lg border border-amber-100 bg-amber-50 p-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.08em] text-amber-700">
                Piano gratuito
              </p>
              <p className="mt-1 text-sm font-semibold leading-6 text-slate-700">
                Puoi continuare con la card gratuita oppure passare a Plus per
                sbloccare funzioni più complete.
              </p>
            </div>
            <a
              href="mailto:cards@onoranzefunebricloud.com?subject=Attivazione%20Cards%20Plus"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-[#1b8395] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#13526f]"
            >
              <Icon icon="mdi:star-plus-outline" className="text-lg" />
              Passa a Plus
            </a>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderDisabledNotice = () => {
    if (!isCardDisabled) {
      return null;
    }

    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white text-amber-700 ring-1 ring-amber-100">
            <Icon icon="mdi:lock-alert-outline" className="text-2xl" />
          </span>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.08em] text-amber-700">
              Cards disattivata
            </p>
            <h4 className="mt-1 text-base font-extrabold text-slate-950">
              La gestione non è disponibile
            </h4>
            <p className="mt-2 text-sm font-medium leading-6 text-slate-700">
              Questa Cards risulta disattivata. Per capire come riattivarla
              scrivi a cards@onoranzefunebricloud.com.
            </p>
            <a
              href="mailto:cards@onoranzefunebricloud.com?subject=Riattivazione%20Cards"
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#1b8395] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#13526f] md:w-auto"
            >
              <Icon icon="mdi:email-fast-outline" className="text-lg" />
              Contattaci
            </a>
          </div>
        </div>
      </div>
    );
  };

  const trackCardToolEvent = (name, props = {}) => {
    trackSelfServiceEvent(name, {
      ...getAnalyticsEventProps(analyticsContext),
      ...props,
      hasCard: Boolean(hasCard),
      plan: cardPlanMeta.plan || "none",
    });
  };

  const trackAuthenticatedProductEvent = useCallback(
    (name, props = {}) => {
      trackSelfServiceEvent(name, {
        ...getAnalyticsEventProps(analyticsContext),
        plan: cardPlanMeta.plan || "none",
        ...props,
      });
    },
    [analyticsContext, cardPlanMeta.plan],
  );

  useEffect(() => {
    if (onAuthenticatedViewChange) {
      onAuthenticatedViewChange(isAuthenticatedCardView);
    }
  }, [isAuthenticatedCardView, onAuthenticatedViewChange]);

  const scrollToElementId = (elementId) => {
    window.setTimeout(() => {
      document
        .getElementById(elementId)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const goToCardEditor = () => {
    if (isCardDisabled) {
      return;
    }

    setError("");
    setMessage("");
    setCardValidationErrors({});
    setStep(hasCard ? "edit" : "create");
    trackSelfServiceEvent("self_service_quick_action_click", {
      ...getAnalyticsEventProps(analyticsContext),
      action: "edit_card",
    });
    scrollToElementId("editor-card");
  };

  const renderManagerHeader = () => (
    <div
      className={
        isAuthenticatedCardView
          ? "overflow-hidden rounded-xl bg-gradient-to-br from-[#073f3e] via-[#13526f] to-[#1b8395] p-4 text-white shadow-2xl shadow-slate-950/25 ring-1 ring-white/10 sm:p-6"
          : compact
            ? "flex flex-col justify-center rounded-t-xl rounded-b-none bg-white p-4 shadow-xl shadow-slate-950/10 ring-1 ring-slate-200 sm:p-5"
            : "flex flex-col justify-center rounded-t-xl rounded-b-none bg-white p-5 shadow-xl shadow-slate-950/10 ring-1 ring-slate-200 sm:p-6"
      }
    >
      <div
        className={
          isAuthenticatedCardView
            ? "flex flex-col gap-5 md:flex-row md:items-end md:justify-between"
            : ""
        }
      >
        <div className="min-w-0">
          <span
            className={`inline-flex w-fit items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ring-1 ${
              isAuthenticatedCardView
                ? "bg-white/10 text-cyan-100 ring-white/10"
                : "bg-cyan-50 text-[#13526f] ring-cyan-100"
            }`}
          >
            {isAuthenticatedCardView && (
              <Icon icon="mdi:shield-account-outline" className="text-sm" />
            )}
            {isAuthenticatedCardView
              ? "Area personale di gestione card"
              : "Area personale"}
          </span>
          <h2
            className={
              isAuthenticatedCardView
                ? "mt-3 text-[1.35rem] font-extrabold leading-tight text-white sm:text-2xl md:text-3xl"
                : compact
                  ? "mt-3 text-xl font-extrabold leading-tight text-slate-950 sm:text-2xl"
                  : "mt-4 text-3xl font-extrabold leading-tight text-slate-950 sm:text-4xl"
            }
          >
            {isAuthenticatedCardView
              ? `Benvenuto${managerFullName ? `, ${managerFullName}` : ""}`
              : "Gestisci la tua Cards"}
          </h2>
          {isAuthenticatedCardView && (
            <p className="mt-3 max-w-2xl text-sm leading-6 text-cyan-50/85">
              Aggiorna i dati della tua Cards, salva le modifiche e apri il link
              pubblico per controllare subito cosa vedono i tuoi contatti.
            </p>
          )}
          {!isAuthenticatedCardView && (
            <p
              className={
                compact
                  ? "mt-2 text-sm leading-6 text-slate-600"
                  : "mt-4 max-w-md text-sm leading-6 text-slate-600"
              }
            >
              Inserisci l'email associata alla tua Cards: ti invieremo un link
              di gestione e un codice OTP per accedere in modo sicuro.
            </p>
          )}
        </div>

        {isAuthenticatedCardView ? (
          <div className="grid w-full gap-2 md:w-auto md:grid-cols-2">
            {showPublicLink && cardUrl && !isCardDisabled && (
              <a
                href={cardUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  trackSelfServiceEvent("self_service_public_card_opened", {
                    ...getAnalyticsEventProps(analyticsContext),
                    source: "manager_header",
                    hasCard: Boolean(hasCard),
                  })
                }
                className="inline-flex items-center justify-center gap-2 rounded-md bg-white px-4 py-3 text-sm font-bold text-[#13526f] transition hover:bg-cyan-50"
              >
                <Icon icon="mdi:open-in-new" className="text-lg" />
                Apri Cards
              </a>
            )}
            <button
              type="button"
              onClick={logout}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white/10 px-4 py-3 text-sm font-bold text-white ring-1 ring-white/20 transition hover:bg-white/15"
            >
              <Icon icon="mdi:logout" className="text-lg" />
              Esci
            </button>
          </div>
        ) : showPublicLink && cardUrl && !isCardDisabled ? (
          <a
            href={cardUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() =>
              trackSelfServiceEvent("self_service_public_card_opened", {
                ...getAnalyticsEventProps(analyticsContext),
                source: "manager_overview",
                hasCard: Boolean(hasCard),
              })
            }
            className={`inline-flex w-fit items-center gap-2 rounded-md px-4 py-3 text-sm font-semibold ring-1 transition ${
              isAuthenticatedCardView
                ? "bg-white/10 text-white ring-white/20 hover:bg-white/15"
                : "bg-[#1b8395] text-white ring-[#1b8395] hover:bg-[#13526f]"
            } ${isAuthenticatedCardView ? "shrink-0" : "mt-6"}`}
          >
            <Icon icon="mdi:open-in-new" className="text-lg" />
            Apri Cards
          </a>
        ) : null}
      </div>

      {isAuthenticatedCardView && !isCardDisabled && (
        <div className="mt-5 grid gap-2 border-t border-white/10 pt-5 sm:grid-cols-3">
          <button
            type="button"
            onClick={goToCardEditor}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-white/10 px-4 py-3 text-sm font-bold text-white ring-1 ring-white/20 transition hover:bg-white/15"
          >
            <Icon icon="mdi:pencil-outline" className="text-lg" />
            Edit card
          </button>
          <a
            href="#strumento-firma-email"
            onClick={() =>
              trackSelfServiceEvent("self_service_quick_action_click", {
                ...getAnalyticsEventProps(analyticsContext),
                action: "email_signature",
              })
            }
            className="inline-flex items-center justify-center gap-2 rounded-md bg-white/10 px-4 py-3 text-sm font-bold text-white ring-1 ring-white/20 transition hover:bg-white/15"
          >
            <Icon icon="mdi:email-edit-outline" className="text-lg" />
            Generatore firma
          </a>
          <a
            href="#strumento-qr-code"
            onClick={() =>
              trackSelfServiceEvent("self_service_quick_action_click", {
                ...getAnalyticsEventProps(analyticsContext),
                action: "qr_code",
              })
            }
            className="inline-flex items-center justify-center gap-2 rounded-md bg-white/10 px-4 py-3 text-sm font-bold text-white ring-1 ring-white/20 transition hover:bg-white/15"
          >
            <Icon icon="mdi:qrcode" className="text-lg" />
            QR code
          </a>
        </div>
      )}
    </div>
  );

  return (
    <div
      className={
        isAuthenticatedCardView
          ? "grid gap-5"
          : compact
            ? "grid gap-0"
            : "grid gap-0"
      }
    >
      {renderManagerHeader()}

      {isAuthenticatedCardView && hasCard && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(220px,0.45fr)]">
          {isCardDisabled ? renderDisabledNotice() : renderPlanNotice()}
          <div className="rounded-lg bg-white p-4 shadow-xl shadow-slate-950/10 ring-1 ring-slate-200">
            <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
              Stato profilo
            </p>
            <div className="mt-3 grid gap-3">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-emerald-50 text-emerald-700">
                  <Icon icon="mdi:check-decagram-outline" className="text-xl" />
                </span>
                <div>
                  <p className="text-sm font-extrabold text-slate-900">
                    {isCardDisabled ? "Accesso limitato" : "Accesso verificato"}
                  </p>
                  <p className="text-xs font-medium text-slate-500">
                    {isCardDisabled
                      ? "Cards disattivata"
                      : "Sessione attiva con OTP"}
                  </p>
                </div>
              </div>
              {cardPlanMeta.plan && (
                <div className="rounded-md bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600 ring-1 ring-slate-200">
                  Piano:{" "}
                  <span className="text-slate-950">
                    {cardPlanMeta.plan === "plus" ? "Plus" : "Gratuito"}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        className={
          isAuthenticatedCardView
            ? "rounded-xl bg-white p-4 shadow-2xl shadow-slate-950/15 ring-1 ring-slate-200 sm:p-6"
            : compact
              ? "-mt-px rounded-t-none rounded-b-xl bg-white p-4 shadow-xl shadow-slate-950/10 ring-1 ring-slate-200 sm:p-5"
              : "-mt-px rounded-t-none rounded-b-xl bg-white p-5 shadow-xl shadow-slate-950/10 ring-1 ring-slate-200 sm:p-6"
        }
      >
        {(message || error) && (
          <div
            className={`mb-4 whitespace-pre-line rounded-md px-4 py-3 text-sm font-medium leading-6 ${
              error
                ? "bg-red-50 text-red-700"
                : "bg-emerald-50 text-emerald-700"
            }`}
          >
            {error || message}
          </div>
        )}

        {step === "confirmEmail" && (
          <div className="space-y-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                Email della card
              </p>
              <div className="mt-2 flex items-center gap-3 rounded-lg bg-slate-50 px-4 py-3 ring-1 ring-slate-200">
                <Icon
                  icon="mdi:email-lock-outline"
                  className="shrink-0 text-xl text-[#1b8395]"
                />
                <span className="min-w-0 truncate font-mono text-sm font-bold text-slate-800">
                  {maskEmail(email)}
                </span>
              </div>
            </div>
            <p className="text-sm leading-6 text-slate-600">
              Verifichiamo la mail e inviamo un link di accesso e un codice OTP
              all'email impostata sulla Cards.
            </p>
            <button
              type="button"
              onClick={requestOtpCode}
              disabled={loading || !apiBaseUrl}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#1b8395] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#13526f] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Icon icon="mdi:send-lock" className="text-lg" />
              {loading ? "Invio..." : "Invia link e codice"}
            </button>
          </div>
        )}

        {step === "email" && (
          <form onSubmit={submitEmail} className="space-y-4">
            <div>
              <label
                htmlFor="card-manager-email"
                className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500"
              >
                Email del profilo attivo
              </label>
              <input
                id="card-manager-email"
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="mt-2 w-full rounded-md border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-600 focus:ring-2 focus:ring-cyan-100"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !apiBaseUrl}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#1b8395] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#13526f] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Icon icon="mdi:email-fast" className="text-lg" />
              Ricevi link di gestione e codice OTP
            </button>
          </form>
        )}

        {step === "code" && (
          <form onSubmit={submitCode} className="space-y-4">
            <p className="rounded-md bg-cyan-50 px-4 py-3 text-sm font-medium leading-6 text-[#13526f] ring-1 ring-cyan-100">
              Inserisci il codice ricevuto via mail per accedere alla gestione
              della tua Cards.
            </p>
            <div>
              <label
                htmlFor="card-manager-code"
                className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500"
              >
                Codice ricevuto
                <span className="normal-case tracking-normal text-slate-400">
                  {" "}
                  (controlla anche spam o posta indesiderata)
                </span>
              </label>
              <input
                id="card-manager-code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                required
                value={code}
                onChange={(event) => setCode(event.target.value)}
                className="mt-2 w-full rounded-md border border-slate-200 px-4 py-3 text-center text-2xl font-bold tracking-[0.2em] text-slate-900 outline-none transition focus:border-cyan-600 focus:ring-2 focus:ring-cyan-100"
                maxLength={6}
                placeholder="000000"
              />
            </div>
            {/* SMS OTP fallback disabilitato temporaneamente — verrà riattivato in futuro.
            <div className="rounded-lg bg-slate-50 p-3 ring-1 ring-slate-200">
              <button
                type="button"
                onClick={() => setSmsFallbackOpen((current) => !current)}
                className="inline-flex w-full items-center justify-between gap-3 text-left text-sm font-bold text-[#13526f]"
                aria-expanded={smsFallbackOpen}
              >
                <span>OTP non ricevuto? Prova con SMS</span>
                <Icon
                  icon={smsFallbackOpen ? "mdi:chevron-up" : "mdi:chevron-down"}
                  className="text-xl"
                />
              </button>
              {smsFallbackOpen && (
                <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                  <label className="block">
                    <span className="sr-only">
                      Numero cellulare per OTP SMS
                    </span>
                    <input
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={smsPhone}
                      onChange={(event) => setSmsPhone(event.target.value)}
                      className="w-full rounded-md border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-600 focus:ring-2 focus:ring-cyan-100"
                      placeholder="+39 333 1234567"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={requestSmsOtpCode}
                    disabled={smsLoading || !apiBaseUrl}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-white px-4 py-3 text-sm font-bold text-[#13526f] ring-1 ring-cyan-200 transition hover:bg-cyan-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Icon icon="mdi:message-lock-outline" className="text-lg" />
                    {smsLoading ? "Invio..." : "Invia SMS"}
                  </button>
                </div>
              )}
            </div>
            */}
            <div
              className={
                compact
                  ? "grid gap-3 md:grid-cols-2"
                  : "grid gap-3 sm:grid-cols-2"
              }
            >
              <button
                type="button"
                onClick={() =>
                  setStep(shouldStartWithMaskedEmail ? "confirmEmail" : "email")
                }
                className="rounded-md border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                {shouldStartWithMaskedEmail
                  ? "Richiedi nuovo codice"
                  : "Annulla"}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#1b8395] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#13526f] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Icon icon="mdi:lock-open-check" className="text-lg" />
                Entra
              </button>
            </div>
          </form>
        )}

        {step === "checking" && (
          <div className="space-y-5">
            <div className="flex items-center gap-3 rounded-lg bg-slate-50 px-4 py-4 ring-1 ring-slate-200">
              <span className="h-3 w-3 animate-pulse rounded-full bg-[#1b8395]" />
              <p className="text-sm font-semibold text-slate-700">
                Verifico la tua sessione...
              </p>
            </div>
          </div>
        )}

        {step === "overview" && (
          <div className="space-y-5">
            <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200 sm:p-5">
              <p className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-cyan-700 ring-1 ring-slate-200">
                <Icon
                  icon={
                    hasCard
                      ? "mdi:card-account-details-outline"
                      : "mdi:plus-circle-outline"
                  }
                  className="text-sm"
                />
                {hasCard ? "La tua card" : "Nuova card"}
              </p>
              <h3 className="mt-3 text-2xl font-extrabold leading-tight text-slate-950">
                {hasCard ? "La tua Cards" : "Crea la tua Cards gratuita"}
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                {hasCard
                  ? "Questa è la tua area privata: da qui modifichi i dati pubblici della card e controlli il link condiviso."
                  : "Questa email è verificata: completa i dati essenziali e genera il link pubblico."}
              </p>
              {cardUrl && (
                <a
                  href={cardUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={`mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md px-3 py-3 text-sm font-bold ring-1 transition md:w-auto ${
                    isCardDisabled
                      ? "pointer-events-none bg-slate-100 text-slate-400 ring-slate-200"
                      : "bg-white text-[#13526f] ring-slate-200 hover:bg-cyan-50"
                  }`}
                  aria-disabled={isCardDisabled}
                >
                  <Icon icon="mdi:open-in-new" className="text-lg" />
                  {isCardDisabled
                    ? "Card pubblica disattivata"
                    : "Apri card pubblica"}
                </a>
              )}
            </div>

            {!isCardDisabled && (
              <button
                type="button"
                onClick={() => {
                  setError("");
                  setMessage("");
                  setCardValidationErrors({});
                  setStep(hasCard ? "edit" : "create");
                }}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#1b8395] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#13526f]"
              >
                <Icon
                  icon={
                    hasCard
                      ? "mdi:pencil-outline"
                      : "mdi:card-account-details-star-outline"
                  }
                  className="text-lg"
                />
                {hasCard ? "Modifica card" : "Crea card"}
              </button>
            )}

            <button
              type="button"
              onClick={logout}
              className="w-full rounded-md border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 sm:hidden"
            >
              Usa un'altra email
            </button>
          </div>
        )}

        {(step === "edit" || step === "create") && !isCardDisabled && (
          <form
            id="editor-card"
            onSubmit={step === "create" ? createCard : updateCard}
            className="space-y-5"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-cyan-700">
                  {step === "create" ? "Nuova card" : "Editor card"}
                </p>
                <h3 className="mt-1 text-xl font-extrabold text-slate-950">
                  {step === "create"
                    ? "Compila i dati principali"
                    : "Dati della card"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setStep("overview")}
                className="rounded-md border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Indietro
              </button>
            </div>

            <div className="space-y-6 overflow-visible pr-0">
              {editableFieldGroups.map((group) => (
                <fieldset key={group.title} className="space-y-3">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <legend className="inline-flex items-center gap-2 text-sm font-bold text-slate-800">
                      <span className="flex h-8 w-8 items-center justify-center rounded-md bg-cyan-50 text-[#1b8395]">
                        <Icon
                          icon={
                            sectionIcons[group.title] || "mdi:pencil-outline"
                          }
                          className="text-lg"
                        />
                      </span>
                      <span>{group.title}</span>
                    </legend>
                    {((group.title === "Dati aziendali" && hasPlusPlan) ||
                      group.title === "Dati di fatturazione") && (
                      <div className="flex flex-wrap gap-2">
                        {hasPlusPlan && (
                          <button
                            type="button"
                            onClick={() => setIsVatLookupOpen(true)}
                            className="inline-flex w-fit items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-800 transition hover:bg-emerald-100"
                          >
                            <Icon icon="mdi:magnify" className="text-base" />
                            Cerca da P.IVA
                          </button>
                        )}
                        {group.title === "Dati di fatturazione" && (
                          <button
                            type="button"
                            onClick={copyCompanyAddressToBilling}
                            disabled={!hasCompanyAddressData}
                            className="inline-flex w-fit items-center gap-2 rounded-md border border-cyan-100 bg-cyan-50 px-3 py-2 text-xs font-bold text-[#13526f] transition hover:bg-cyan-100 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Icon
                              icon="mdi:content-copy"
                              className="text-base"
                            />
                            Copia indirizzo aziendale
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div
                    className={
                      compact
                        ? "grid gap-3 md:grid-cols-2"
                        : "grid gap-3 md:grid-cols-2"
                    }
                  >
                    {group.fields.map(
                      ([
                        name,
                        label,
                        type = "text",
                        helpText = "",
                        prefixFieldName = "",
                      ]) => {
                        const fieldError = cardValidationErrors[name];
                        const fieldBorderClass = fieldError
                          ? "border-red-300 focus-within:border-red-500 focus-within:ring-red-100"
                          : "border-slate-200 focus-within:border-cyan-600 focus-within:ring-cyan-100";
                        const inputBorderClass = fieldError
                          ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                          : "border-slate-200 focus:border-cyan-600 focus:ring-cyan-100";

                        if (imageUploadFields.has(name)) {
                          return renderImageUploadField({
                            name,
                            label,
                            helpText,
                          });
                        }

                        return (
                          <label
                            key={name}
                            className={`block ${
                              type === "textarea" ? "md:col-span-2" : ""
                            }`}
                          >
                            <span className="text-xs font-semibold text-slate-500">
                              {label}
                            </span>
                            {prefixFieldName ? (
                              <div
                                className={`mt-1 grid grid-cols-[96px_minmax(0,1fr)] overflow-hidden rounded-md border focus-within:ring-2 md:grid-cols-[116px_minmax(0,1fr)] ${fieldBorderClass}`}
                              >
                                <select
                                  value={form[prefixFieldName] || "+39"}
                                  onChange={(event) =>
                                    setForm((current) => ({
                                      ...current,
                                      [prefixFieldName]: event.target.value,
                                    }))
                                  }
                                  className="min-w-0 border-0 border-r border-slate-200 bg-slate-50 px-2 py-3 text-xs font-bold text-slate-700 outline-none md:px-2.5 md:text-sm"
                                  aria-label={`Prefisso ${label}`}
                                >
                                  {phonePrefixOptions.map(
                                    ([value, optionLabel]) => (
                                      <option key={value} value={value}>
                                        {optionLabel}
                                      </option>
                                    ),
                                  )}
                                </select>
                                <input
                                  type={type}
                                  required={requiredCardFields.has(name)}
                                  value={form[name] || ""}
                                  onChange={(event) =>
                                    updateCardField(name, event.target.value)
                                  }
                                  aria-invalid={Boolean(fieldError)}
                                  className="min-w-0 w-full border-0 px-3 py-3 text-sm text-slate-900 outline-none md:py-2.5"
                                />
                              </div>
                            ) : type === "textarea" ? (
                              <>
                                <textarea
                                  required={requiredCardFields.has(name)}
                                  disabled={
                                    step === "create" && name === "email"
                                  }
                                  value={form[name] || ""}
                                  maxLength={fieldMaxLengths[name]}
                                  rows={4}
                                  onChange={(event) =>
                                    updateCardField(name, event.target.value)
                                  }
                                  aria-invalid={Boolean(fieldError)}
                                  className={`mt-1 min-h-[108px] w-full resize-none rounded-md border px-3 py-3 text-sm leading-6 text-slate-900 outline-none transition focus:ring-2 disabled:bg-slate-100 disabled:text-slate-500 md:py-2.5 ${inputBorderClass}`}
                                />
                                {fieldMaxLengths[name] && (
                                  <span className="mt-1 block text-right text-[11px] font-medium leading-4 text-slate-400">
                                    {(form[name] || "").length}/
                                    {fieldMaxLengths[name]}
                                  </span>
                                )}
                              </>
                            ) : (
                              <input
                                type={
                                  name === "email"
                                    ? "email"
                                    : type === "url"
                                      ? "text"
                                      : type
                                }
                                inputMode={type === "url" ? "url" : undefined}
                                required={requiredCardFields.has(name)}
                                disabled={step === "create" && name === "email"}
                                value={form[name] || ""}
                                onChange={(event) =>
                                  updateCardField(name, event.target.value)
                                }
                                aria-invalid={Boolean(fieldError)}
                                maxLength={fieldMaxLengths[name]}
                                className={`mt-1 w-full rounded-md border px-3 py-3 text-sm text-slate-900 outline-none transition focus:ring-2 disabled:bg-slate-100 disabled:text-slate-500 md:py-2.5 ${inputBorderClass}`}
                              />
                            )}
                            {fieldError && (
                              <span className="mt-1 block text-[11px] font-bold leading-4 text-red-600">
                                {fieldError}
                              </span>
                            )}
                            {helpText && (
                              <span className="mt-1 block text-[11px] font-medium leading-4 text-slate-400">
                                {helpText}
                              </span>
                            )}
                          </label>
                        );
                      },
                    )}
                  </div>
                  {group.title === "Dati aziendali" && hasPlusPlan && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p className="inline-flex items-center gap-2 text-sm font-bold text-slate-800">
                            <Icon
                              icon="mdi:share-variant-outline"
                              className="text-lg text-[#1b8395]"
                            />
                            Social
                          </p>
                          <p className="mt-1 text-xs font-medium leading-5 text-slate-500">
                            Aggiungi solo i profili che vuoi mostrare nella
                            Cards.
                          </p>
                        </div>
                        {availableSocialFields.length > 0 && (
                          <div className="grid gap-2 md:grid-cols-[minmax(0,190px)_auto]">
                            <select
                              value={selectedSocialField}
                              onChange={(event) =>
                                setSelectedSocialField(event.target.value)
                              }
                              className="w-full rounded-md border border-slate-200 bg-white px-3 py-3 text-sm font-semibold text-slate-700 outline-none transition focus:border-cyan-600 focus:ring-2 focus:ring-cyan-100 md:py-2.5"
                              aria-label="Social da aggiungere"
                            >
                              <option value="">Scegli social</option>
                              {availableSocialFields.map(([name, label]) => (
                                <option key={name} value={name}>
                                  {label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={addSocialField}
                              disabled={!selectedSocialField}
                              className="inline-flex items-center justify-center gap-2 rounded-md bg-[#1b8395] px-3 py-3 text-xs font-bold text-white transition hover:bg-[#13526f] disabled:cursor-not-allowed disabled:opacity-50 md:py-2.5"
                            >
                              <Icon icon="mdi:plus" className="text-base" />
                              Aggiungi
                            </button>
                          </div>
                        )}
                      </div>

                      {visibleSocialFields.length > 0 ? (
                        <div className="mt-3 grid gap-3">
                          {visibleSocialFields.map(([name, label, icon]) => {
                            const fieldError = cardValidationErrors[name];
                            const inputBorderClass = fieldError
                              ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                              : "border-slate-200 focus:border-cyan-600 focus:ring-cyan-100";

                            return (
                              <label key={name} className="block">
                                <span className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500">
                                  <Icon
                                    icon={icon}
                                    className="text-base text-[#1b8395]"
                                  />
                                  {label}
                                </span>
                                <div className="mt-1 grid gap-2 md:grid-cols-[minmax(0,1fr)_auto]">
                                  <input
                                    type="text"
                                    inputMode="url"
                                    value={form[name] || ""}
                                    onChange={(event) =>
                                      updateCardField(name, event.target.value)
                                    }
                                    aria-invalid={Boolean(fieldError)}
                                    className={`w-full rounded-md border bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:ring-2 md:py-2.5 ${inputBorderClass}`}
                                    placeholder={`URL ${label}`}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeSocialField(name)}
                                    className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-3 text-xs font-bold text-slate-600 transition hover:bg-slate-50 md:py-2.5"
                                    aria-label={`Rimuovi ${label}`}
                                  >
                                    <Icon
                                      icon="mdi:trash-can-outline"
                                      className="text-base"
                                    />
                                    Rimuovi
                                  </button>
                                </div>
                                {fieldError && (
                                  <span className="mt-1 block text-[11px] font-bold leading-4 text-red-600">
                                    {fieldError}
                                  </span>
                                )}
                                {socialFieldDomainHints[name] && (
                                  <span className="mt-1 block text-[11px] font-medium leading-4 text-slate-400">
                                    Dominio accettato:{" "}
                                    {socialFieldDomainHints[name]}.
                                  </span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="mt-3 rounded-md bg-white px-3 py-3 text-xs font-semibold text-slate-500 ring-1 ring-slate-200">
                          Nessun social aggiunto.
                        </p>
                      )}
                    </div>
                  )}
                </fieldset>
              ))}
              {hasPlusPlan && (
                <CardLookAndFeelEditor
                  form={form}
                  errors={cardValidationErrors}
                  onPresetChange={updateThemePreset}
                  onThemeFieldChange={updateThemeField}
                />
              )}
              {renderPlusUpgradeNotice()}
            </div>

            {error && (
              <div className="whitespace-pre-line rounded-md bg-red-50 px-4 py-3 text-sm font-semibold leading-6 text-red-700 ring-1 ring-red-100">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || isUploadingImage}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#1b8395] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#13526f] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Icon icon="mdi:content-save-check" className="text-lg" />
              {isUploadingImage
                ? "Caricamento immagine..."
                : step === "create"
                  ? "Crea card"
                  : "Salva modifiche"}
            </button>
          </form>
        )}
      </div>
      {isAuthenticatedCardView && hasCard && !isCardDisabled && (
        <CardToolsPanel
          card={form}
          cardUrl={cardUrl}
          hasPlusPlan={hasPlusPlan}
          onTrack={trackCardToolEvent}
        />
      )}
      <AvatarAiModal
        open={isAvatarAiModalOpen}
        onClose={() => setIsAvatarAiModalOpen(false)}
        token={token}
        currentAvatarUrl={form.avatarUrl}
        onTrack={trackAuthenticatedProductEvent}
        onApplied={(newAvatarUrl) => {
          if (newAvatarUrl) {
            updateCardField("avatarUrl", newAvatarUrl);
          }
          setMessage("Avatar AI applicato.");
          setIsAvatarAiModalOpen(false);
        }}
        onAvatarReplaced={(url) => {
          updateCardField("avatarUrl", url);
          setUploadState((current) => ({
            ...current,
            avatarUrl: { status: "done", progress: 100 },
          }));
          setMessage("Avatar aggiornato.");
        }}
        onAvatarRemoved={() => {
          updateCardField("avatarUrl", "");
          setUploadState((current) => ({
            ...current,
            avatarUrl: { status: "idle", progress: 0 },
          }));
        }}
        onBuyCredits={() => setIsBuyCreditsModalOpen(true)}
      />
      <VatLookupDialog
        open={isVatLookupOpen}
        onClose={() => setIsVatLookupOpen(false)}
        token={token}
        onAccept={applyVatLookupResult}
        onTrack={trackAuthenticatedProductEvent}
      />
      <BuyCreditsModal
        open={isBuyCreditsModalOpen}
        onClose={() => setIsBuyCreditsModalOpen(false)}
        token={token}
        card={form}
        onTrack={trackAuthenticatedProductEvent}
        onSuccess={() => {
          setMessage("Acquisto completato. Crediti accreditati.");
        }}
      />
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <span className="rounded-full bg-cyan-100 px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#13526f]">
      {children}
    </span>
  );
}

function CardLookAndFeelEditor({
  form,
  errors = {},
  onPresetChange,
  onThemeFieldChange,
}) {
  const theme = getCardTheme(form);
  const previewContactRows = [
    ["mdi:cellphone", "Cellulare"],
    ["mdi:email", "Email"],
    ["mdi:whatsapp", "WhatsApp"],
  ];
  const colorFields = [
    ["themePrimary", "Principale"],
    ["themeSecondary", "Secondario"],
    ["themeAccent", "Accento CTA"],
    ["themeBackground", "Sfondo"],
    ["themeSurface", "Superficie"],
    ["themeText", "Testo"],
  ];

  return (
    <fieldset className="space-y-4 rounded-xl border border-cyan-100 bg-cyan-50/40 p-4 sm:p-5">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <legend className="inline-flex items-center gap-2 text-sm font-bold text-slate-800">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-white text-[#1b8395] ring-1 ring-cyan-100">
              <Icon icon="mdi:palette-outline" className="text-lg" />
            </span>
            Look & feel della card
          </legend>
          <p className="mt-2 text-xs font-medium leading-5 text-slate-500">
            Personalizza colori, font e angoli della card pubblica. Le card
            senza personalizzazioni continuano a usare il default OFC.
          </p>
        </div>
        <span className="w-fit rounded-full bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-cyan-700 ring-1 ring-cyan-100">
          Plus
        </span>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
              Palette
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {cardThemePresets.map((preset) => {
                const selected =
                  form.themePreset === preset.id ||
                  (!form.themePreset && preset.id === "ofc-default");

                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => onPresetChange(preset.id)}
                    className={`rounded-lg bg-white p-3 text-left transition ${
                      selected
                        ? "ring-2 ring-[#1b8395]"
                        : "ring-1 ring-slate-200 hover:ring-cyan-200"
                    }`}
                  >
                    <span className="block text-sm font-extrabold text-slate-900">
                      {preset.label}
                    </span>
                    <span className="mt-1 block text-xs font-medium leading-5 text-slate-500">
                      {preset.description}
                    </span>
                    <span className="mt-3 flex gap-1">
                      {Object.values(preset.colors)
                        .slice(0, 5)
                        .map((color) => (
                          <span
                            key={color}
                            className="h-5 w-5 rounded-full ring-1 ring-slate-200"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
              Palette personalizzata
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {colorFields.map(([fieldName, label]) => {
                const fieldError = errors[fieldName];
                const value = form[fieldName] || "";

                return (
                  <label key={fieldName} className="block">
                    <span className="text-xs font-semibold text-slate-500">
                      {label}
                    </span>
                    <div
                      className={`mt-1 grid grid-cols-[48px_minmax(0,1fr)] overflow-hidden rounded-md border bg-white focus-within:ring-2 ${
                        fieldError
                          ? "border-red-300 focus-within:border-red-500 focus-within:ring-red-100"
                          : "border-slate-200 focus-within:border-cyan-600 focus-within:ring-cyan-100"
                      }`}
                    >
                      <input
                        type="color"
                        value={isValidHexColor(value) ? value : "#000000"}
                        onChange={(event) =>
                          onThemeFieldChange(fieldName, event.target.value)
                        }
                        className="h-12 w-12 cursor-pointer border-0 bg-transparent p-1"
                        aria-label={label}
                      />
                      <input
                        type="text"
                        value={value}
                        onChange={(event) =>
                          onThemeFieldChange(fieldName, event.target.value)
                        }
                        className="min-w-0 border-0 px-3 py-3 text-sm font-bold text-slate-800 outline-none"
                      />
                    </div>
                    {fieldError && (
                      <span className="mt-1 block text-[11px] font-bold leading-4 text-red-600">
                        {fieldError}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-semibold text-slate-500">Font</span>
              <select
                value={form.themeFont || "system"}
                onChange={(event) =>
                  onThemeFieldChange("themeFont", event.target.value)
                }
                className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-3 text-sm font-bold text-slate-800 outline-none transition focus:border-cyan-600 focus:ring-2 focus:ring-cyan-100"
              >
                {cardThemeFonts.map((font) => (
                  <option key={font.id} value={font.id}>
                    {font.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-xs font-semibold text-slate-500">
                Stile card
              </span>
              <select
                value={form.themeRadius || "rounded"}
                onChange={(event) =>
                  onThemeFieldChange("themeRadius", event.target.value)
                }
                className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-3 text-sm font-bold text-slate-800 outline-none transition focus:border-cyan-600 focus:ring-2 focus:ring-cyan-100"
              >
                {cardThemeRadiusOptions.map((radius) => (
                  <option key={radius.id} value={radius.id}>
                    {radius.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
          <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
            Anteprima rapida
          </p>
          <div
            className={`mt-3 overflow-hidden shadow-sm ring-1 ring-slate-200 ${theme.radius.cardClassName}`}
            style={{
              backgroundColor: theme.colors.surface,
              color: theme.colors.text,
              fontFamily: theme.font.family,
            }}
          >
            <div
              className="px-4 py-5 text-center"
              style={{
                background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary})`,
              }}
            >
              <div
                className={`mx-auto flex h-14 w-14 items-center justify-center bg-white/20 text-white ring-2 ring-white/25 ${theme.radius.avatarClassName}`}
              >
                <Icon icon="mdi:account" className="text-3xl" />
              </div>
              <p className="mt-3 text-lg font-extrabold text-white">
                {`${form.firstName || "Mario"} ${form.lastName || "Rossi"}`}
              </p>
              <p className="text-xs font-semibold text-white/85">
                {form.role || "Consulente"}
              </p>
            </div>
            <div className="space-y-2 p-4">
              <button
                type="button"
                className={`w-full px-3 py-3 text-sm font-bold text-white ${theme.radius.buttonClassName}`}
                style={{ backgroundColor: theme.colors.secondary }}
              >
                Aggiungi ai contatti
              </button>
              {previewContactRows.map(([icon, label]) => (
                <div
                  key={label}
                  className={`flex items-center gap-3 p-3 ${theme.radius.panelClassName}`}
                  style={{ backgroundColor: `${theme.colors.background}` }}
                >
                  <span
                    className={`flex h-9 w-9 items-center justify-center ${theme.radius.iconClassName}`}
                    style={{ backgroundColor: `${theme.colors.secondary}22` }}
                  >
                    <Icon
                      icon={icon}
                      className="text-xl"
                      style={{ color: theme.colors.secondary }}
                    />
                  </span>
                  <span className="text-sm font-semibold">{label}</span>
                </div>
              ))}
              <button
                type="button"
                className={`w-full px-3 py-3 text-sm font-bold text-black ${theme.radius.buttonClassName}`}
                style={{ backgroundColor: theme.colors.accent }}
              >
                Chiama ora
              </button>
            </div>
          </div>
        </div>
      </div>
    </fieldset>
  );
}

function CardPreviewLinkBlock() {
  return (
    <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-200/70 md:p-6">
      <div className="grid gap-6 lg:grid-cols-[1fr_340px] lg:items-center">
        <div>
          <p className="inline-flex items-center gap-2 rounded-full bg-cyan-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-cyan-700 ring-1 ring-cyan-100">
            <Icon icon="mdi:card-account-details-outline" className="text-sm" />
            Anteprima card
          </p>
          <h3 className="mt-3 text-2xl font-extrabold leading-tight text-slate-950">
            Guarda una Cards completa in una pagina dedicata.
          </h3>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600">
            Per vedere la card senza forzare interazioni nella landing, apri una
            demo con dati inventati in una nuova scheda. La pagina riprende la
            struttura reale della card pubblica: header, azioni rapide, contatti
            personali, dati aziendali e QR.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href="/anteprima"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-[#1b8395] px-4 py-3 text-sm font-extrabold text-white transition hover:bg-[#13526f]"
            >
              <Icon icon="mdi:open-in-new" className="text-lg" />
              Apri anteprima card
            </a>
            <a
              href="#piani"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50"
            >
              <Icon icon="mdi:table-eye" className="text-lg" />
              Vedi piani
            </a>
          </div>
        </div>

        <a
          href="/anteprima"
          target="_blank"
          rel="noreferrer"
          className="block rounded-2xl bg-slate-50 p-4 transition hover:bg-cyan-50"
          aria-label="Apri anteprima card in nuova scheda"
        >
          <div className="overflow-hidden rounded-2xl bg-white shadow-lg ring-1 ring-slate-200">
            <div className="bg-gradient-to-r from-[#1b8395] to-[#20acad] px-5 py-6 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white/20 text-white ring-2 ring-white/25">
                <Icon icon="mdi:account" className="text-3xl" />
              </div>
              <p className="mt-3 text-lg font-extrabold text-white">
                Luca Ferri
              </p>
              <p className="text-xs font-semibold text-cyan-50">
                Consulente commerciale
              </p>
            </div>
            <div className="space-y-3 p-5">
              <div className="rounded-xl bg-[#20acad] py-3 text-center text-sm font-bold text-white">
                Aggiungi ai contatti
              </div>
              {[
                ["mdi:cellphone", "Cellulare"],
                ["mdi:email", "Email"],
                ["mdi:whatsapp", "WhatsApp"],
              ].map(([icon, label]) => (
                <div
                  key={label}
                  className="flex items-center gap-3 rounded-xl bg-slate-50 p-3"
                >
                  <Icon icon={icon} className="text-xl text-[#20acad]" />
                  <span className="text-sm font-semibold text-slate-700">
                    {label}
                  </span>
                </div>
              ))}
              <div className="flex items-center gap-3 rounded-xl bg-white p-3 ring-1 ring-slate-200">
                <Icon icon="mdi:qrcode" className="text-3xl text-slate-900" />
                <span className="text-xs font-bold text-slate-600">
                  QR e dati aziendali
                </span>
              </div>
            </div>
          </div>
        </a>
      </div>
    </div>
  );
}

function SignatureGeneratorMockup() {
  const templates = [
    { id: "minimal", label: "Minimal" },
    { id: "corporate", label: "Corporate" },
    { id: "cta", label: "CTA" },
  ];
  const features = [
    {
      id: "template",
      label: "Template",
      icon: "mdi:view-dashboard-outline",
      text: "Cambia modello senza riscrivere i dati.",
    },
    {
      id: "fields",
      label: "Campi",
      icon: "mdi:checkbox-marked-outline",
      text: "Scegli cosa mostrare nella firma.",
    },
    {
      id: "qr",
      label: "QR",
      icon: "mdi:qrcode",
      text: "Aggiungi il QR della card quando serve.",
    },
  ];
  const [activeTemplate, setActiveTemplate] = useState(templates[1]);
  const [activeFeature, setActiveFeature] = useState(features[0]);

  return (
    <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-200/70">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="inline-flex items-center gap-2 rounded-full bg-cyan-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-cyan-700 ring-1 ring-cyan-100">
            <Icon icon="mdi:email-edit-outline" className="text-sm" />
            Anteprima generatore firma
          </p>
          <h3 className="mt-1 text-xl font-extrabold text-slate-950">
            Firma email pronta da copiare
          </h3>
        </div>
        <span className="rounded-full bg-cyan-50 px-3 py-1 text-xs font-bold text-[#13526f]">
          Plus
        </span>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
        <div className="space-y-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
              Template
            </p>
            <div className="mt-2 grid gap-2">
              {templates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => setActiveTemplate(template)}
                  className={`rounded-md px-3 py-2 text-left text-sm font-bold transition ${
                    activeTemplate.id === template.id
                      ? "bg-[#1b8395] text-white"
                      : "bg-slate-50 text-slate-700 ring-1 ring-slate-200 hover:bg-white"
                  }`}
                >
                  {template.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
              Opzioni
            </p>
            <div className="mt-2 grid gap-2">
              {features.map((feature) => (
                <button
                  key={feature.id}
                  type="button"
                  onClick={() => setActiveFeature(feature)}
                  className={`flex items-center gap-3 rounded-md px-3 py-3 text-left transition ${
                    activeFeature.id === feature.id
                      ? "bg-cyan-50 ring-2 ring-cyan-200"
                      : "bg-slate-50 ring-1 ring-slate-200 hover:bg-white"
                  }`}
                >
                  <Icon
                    icon={feature.icon}
                    className="text-lg text-[#1b8395]"
                  />
                  <span>
                    <span className="block text-sm font-extrabold text-slate-900">
                      {feature.label}
                    </span>
                    <span className="block text-xs font-medium leading-5 text-slate-600">
                      {feature.text}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-slate-50 p-4 ring-1 ring-slate-200">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
              Anteprima
            </p>
            <span className="rounded bg-white px-2 py-1 text-[11px] font-bold text-slate-500 ring-1 ring-slate-200">
              {activeTemplate.label}
            </span>
          </div>
          <div className="overflow-hidden rounded-lg bg-white p-4 ring-1 ring-slate-200">
            <div
              className={`border-l-4 pl-4 ${
                activeTemplate.id === "cta"
                  ? "border-[#20acad]"
                  : "border-[#1b8395]"
              }`}
            >
              <div className="flex items-start gap-3">
                {activeTemplate.id !== "minimal" && (
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#1b8395] text-sm font-extrabold text-white">
                    LF
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-base font-extrabold text-slate-950">
                    Luca Ferri
                  </p>
                  <p className="text-xs font-bold text-[#1b8395]">
                    Consulente commerciale
                  </p>
                  <p className="mt-1 text-xs font-semibold text-slate-500">
                    Ferri Servizi Funebri
                  </p>
                  <div className="mt-3 space-y-1 text-xs font-medium leading-5 text-slate-600">
                    <p>Mobile: +39 333 123 4567</p>
                    <p>Email: luca.ferri@example.it</p>
                    <p>Web: www.ferriservizi.example</p>
                  </div>
                  {activeTemplate.id === "cta" && (
                    <div className="mt-3 inline-flex rounded-md bg-[#1b8395] px-3 py-2 text-xs font-extrabold text-white">
                      Salva i miei contatti
                    </div>
                  )}
                </div>
                {activeFeature.id === "qr" && (
                  <div className="ml-auto grid h-16 w-16 shrink-0 grid-cols-4 gap-1 rounded bg-slate-900 p-2">
                    {Array.from({ length: 16 }).map((_, index) => (
                      <span
                        key={index}
                        className={`rounded-[1px] ${
                          index % 2 === 0 ? "bg-white" : "bg-slate-900"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            {["Copia HTML", "Solo testo", "Codice"].map((item, index) => (
              <div
                key={item}
                className={`rounded-md px-3 py-2 text-center text-xs font-extrabold ${
                  index === 0
                    ? "bg-[#1b8395] text-white"
                    : "bg-white text-slate-700 ring-1 ring-slate-200"
                }`}
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AvatarAiMockup() {
  const styles = [
    {
      id: "funeral-director",
      label: "Direttore Funerario",
      type: "Pro",
      description: "Look classico, sobrio e rassicurante.",
      imageUrl: "/avatar-ai-funeral-director.svg",
    },
    {
      id: "ceremony-officiant",
      label: "Cerimoniere",
      type: "Pro",
      description: "Tono solenne, luce calda e composta.",
      imageUrl: "/avatar-ai-ceremony-officiant.svg",
    },
    {
      id: "linkedin-premium",
      label: "LinkedIn Premium",
      type: "Pro",
      description: "Ritratto pulito da profilo professionale.",
      imageUrl: "/avatar-ai-linkedin-premium.svg",
    },
  ];
  const [activeStyle, setActiveStyle] = useState(styles[0]);

  return (
    <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-200/70">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="inline-flex items-center gap-2 rounded-full bg-cyan-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-cyan-700 ring-1 ring-cyan-100">
            <Icon icon="mdi:account-edit-outline" className="text-sm" />
            Anteprima avatar
          </p>
          <h3 className="mt-2 text-xl font-extrabold text-slate-950">
            Da foto caricata ad avatar professionale
          </h3>
          <p className="mt-2 max-w-2xl text-sm leading-7 text-slate-600">
            L'utente carica una foto profilo, sceglie uno stile tra i temi AI
            disponibili e applica la variante scelta alla Cards.
          </p>
        </div>
        <span className="w-fit rounded-full bg-cyan-50 px-3 py-1 text-xs font-bold text-[#13526f]">
          Plus
        </span>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-stretch">
        <div className="rounded-lg bg-slate-50 p-4 ring-1 ring-slate-200">
          <div className="mb-4 flex items-center justify-between gap-3">
            <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
              Trasformazione
            </p>
            <span className="rounded bg-white px-2 py-1 text-[11px] font-bold text-slate-500 ring-1 ring-slate-200">
              {activeStyle.label}
            </span>
          </div>

          <div className="grid items-center gap-3 sm:grid-cols-[1fr_40px_1fr]">
            <div className="overflow-hidden rounded-lg bg-white ring-1 ring-slate-200">
              <div className="aspect-square bg-slate-100">
                <img
                  src="/avatar-ai-original.svg"
                  alt="Foto profilo originale caricata"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="border-t border-slate-200 px-3 py-3">
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
                  Prima
                </p>
                <p className="mt-1 text-sm font-extrabold text-slate-900">
                  Foto caricata
                </p>
              </div>
            </div>

            <div className="flex justify-center text-[#1b8395]">
              <Icon
                icon="mdi:arrow-right-bold"
                className="hidden text-3xl sm:block"
              />
              <Icon icon="mdi:arrow-down-bold" className="text-3xl sm:hidden" />
            </div>

            <div className="overflow-hidden rounded-lg bg-white ring-2 ring-cyan-200">
              <div className="aspect-square bg-slate-100">
                <img
                  src={activeStyle.imageUrl}
                  alt={`Avatar generato stile ${activeStyle.label}`}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="border-t border-cyan-100 px-3 py-3">
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-cyan-700">
                  Dopo
                </p>
                <p className="mt-1 text-sm font-extrabold text-slate-900">
                  {activeStyle.label}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-3">
            {[
              ["mdi:cloud-upload-outline", "Carica foto"],
              ["mdi:creation-outline", "Genera varianti"],
              ["mdi:check-circle-outline", "Applica avatar"],
            ].map(([icon, label]) => (
              <div
                key={label}
                className="flex items-center gap-2 rounded-md bg-white px-3 py-2 text-xs font-extrabold text-slate-700 ring-1 ring-slate-200"
              >
                <Icon icon={icon} className="text-lg text-[#1b8395]" />
                {label}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
            Stili disponibili
          </p>
          {styles.map((style) => (
            <button
              key={style.id}
              type="button"
              onClick={() => setActiveStyle(style)}
              className={`flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition ${
                activeStyle.id === style.id
                  ? "bg-cyan-50 ring-2 ring-cyan-200"
                  : "bg-slate-50 ring-1 ring-slate-200 hover:bg-white"
              }`}
            >
              <span className="h-14 w-14 shrink-0 overflow-hidden rounded-md bg-white ring-1 ring-slate-200">
                <img
                  src={style.imageUrl}
                  alt=""
                  className="h-full w-full object-cover"
                />
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-2">
                  <span className="truncate text-sm font-extrabold text-slate-900">
                    {style.label}
                  </span>
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-extrabold uppercase text-emerald-700">
                    {style.type}
                  </span>
                </span>
                <span className="mt-1 block text-xs font-medium leading-5 text-slate-600">
                  {style.description}
                </span>
              </span>
            </button>
          ))}
          <div className="rounded-lg bg-[#1b8395] px-4 py-3 text-center text-sm font-extrabold text-white">
            Applica alla Cards
          </div>
        </div>
      </div>
    </div>
  );
}

function InteractiveProductMockups() {
  return (
    <section className="px-5 pb-20">
      <div className="mx-auto max-w-6xl">
        <div className="max-w-3xl">
          <SectionLabel>Anteprima prodotto</SectionLabel>
          <h2 className="mt-4 text-3xl font-extrabold leading-tight text-slate-950 sm:text-4xl">
            Vedi cosa contiene una Cards e cosa puoi generare dall'area profilo.
          </h2>
          <p className="mt-4 text-sm leading-7 text-slate-600">
            Gli esempi usano dati fittizi e mostrano come card, QR, avatar e
            firma email lavorano insieme: stessa identità, stessi contatti,
            materiali pronti da condividere.
          </p>
        </div>
        <div className="mt-8 space-y-6">
          <CardPreviewLinkBlock />
          <AvatarAiMockup />
          <SignatureGeneratorMockup />
        </div>
      </div>
    </section>
  );
}

function SignupForm() {
  const apiBaseUrl = useMemo(getApiBaseUrl, []);
  const urlCouponCode = useMemo(() => {
    return getUrlCouponCode();
  }, []);
  const initialCouponCode = useMemo(getInitialCouponCode, []);
  const initialEventAvatarHandoff = useMemo(loadEventAvatarAiSignupHandoff, []);
  const hasInitialCoupon = Boolean(initialCouponCode);
  const [signupForm, setSignupForm] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    email: "",
    phone: "",
    avatarUrl: initialEventAvatarHandoff?.selectedAvatarUrl || "",
    couponCode: hasInitialCoupon ? initialCouponCode : "",
    legalConsent: false,
  });
  const [signupStep, setSignupStep] = useState("form");
  const [signupVerificationCode, setSignupVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState("");
  const [loadingPhraseIndex, setLoadingPhraseIndex] = useState(0);
  const [signupError, setSignupError] = useState("");
  const [signupMessage, setSignupMessage] = useState("");
  const [managementMessage, setManagementMessage] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [signupSmsFallbackOpen, setSignupSmsFallbackOpen] = useState(false);
  const [signupSmsPhone, setSignupSmsPhone] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [signupSmsLoading, setSignupSmsLoading] = useState(false);
  const [eventAvatarHandoff, setEventAvatarHandoff] = useState(
    initialEventAvatarHandoff,
  );
  const [isExistingProfileOpen, setIsExistingProfileOpen] = useState(false);
  const [couponDetails, setCouponDetails] = useState(null);
  const [couponStatus, setCouponStatus] = useState(
    hasInitialCoupon ? "loading" : "idle",
  );
  const [couponMessage, setCouponMessage] = useState(
    hasInitialCoupon ? "Verifico il coupon inserito..." : "",
  );

  useEffect(() => {
    if (initialEventAvatarHandoff) {
      clearEventAvatarAiSignupHandoff();
    }
  }, [initialEventAvatarHandoff]);

  useEffect(() => {
    if (!loading) {
      setLoadingPhraseIndex(0);
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setLoadingPhraseIndex(
        (currentIndex) => (currentIndex + 1) % signupLoadingPhrases.length,
      );
    }, 1400);

    return () => window.clearInterval(intervalId);
  }, [loading]);

  const updateSignupField = (name, value) => {
    setSignupForm((current) => ({
      ...current,
      [name]: value,
    }));
    setSignupError("");
    setSignupMessage("");

    if (name === "couponCode") {
      setCouponDetails(null);
      setCouponStatus("idle");
      setCouponMessage("");
    }
  };

  const clearSignupEventAvatarHandoff = () => {
    const handoffAvatarUrls = new Set(
      [
        eventAvatarHandoff?.selectedAvatarUrl,
        ...(eventAvatarHandoff?.avatars || []).map(
          (avatar) => avatar.resultUrl,
        ),
      ].filter(Boolean),
    );

    clearEventAvatarAiSignupHandoff();
    setEventAvatarHandoff(null);
    setSignupForm((current) => {
      if (!handoffAvatarUrls.has(current.avatarUrl)) {
        return current;
      }

      return {
        ...current,
        avatarUrl: "",
      };
    });
  };

  const eventAvatarChoices = eventAvatarHandoff?.avatars || [];
  const selectedEventAvatar =
    eventAvatarChoices.find(
      (avatar) => avatar.resultUrl === signupForm.avatarUrl,
    ) || eventAvatarChoices[0];

  const selectEventAvatar = (avatar) => {
    if (!avatar?.resultUrl) {
      return;
    }

    updateSignupField("avatarUrl", avatar.resultUrl);
    trackSignupEvent("signup_event_avatar_selected", {
      source: eventAvatarHandoff?.source || "event-avatar-ai",
      themeId: avatar.themeId || "",
    });
  };

  const validateCouponCode = async (couponCode, source = "form") => {
    const normalizedCode = normalizeCouponCode(couponCode);

    if (!normalizedCode) {
      trackSignupEvent("signup_coupon_missing");
      setCouponStatus("error");
      setCouponMessage("Inserisci un codice coupon per applicarlo.");
      return;
    }

    trackSignupEvent("signup_coupon_attempt");
    setCouponStatus("loading");
    setCouponMessage("");
    setCouponDetails(null);

    if (!apiBaseUrl) {
      setCouponStatus("error");
      setCouponMessage("Verifica coupon non disponibile.");
      return;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/self/coupon/validate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ couponCode: normalizedCode }),
      });
      const data = await response.json();

      if (response.ok && data.valid && data.coupon) {
        const coupon = data.coupon;

        setCouponStatus("applied");
        setCouponDetails(coupon);
        setSignupForm((current) => ({
          ...current,
          couponCode: coupon.code || normalizedCode,
        }));
        trackSignupEvent("signup_coupon_applied", {
          source,
          ...getCouponEventProps(coupon, normalizedCode),
        });
        setCouponMessage(
          normalizeCouponDisplayMessage(
            data.message ||
              coupon.publicMessage ||
              "Coupon applicato. Completa l'iscrizione per riscattare le condizioni associate al codice.",
          ),
        );
        return;
      }

      setCouponStatus("error");
      trackSignupEvent("signup_coupon_rejected");
      setCouponMessage(data.message || "Codice coupon non valido.");
    } catch (err) {
      setCouponStatus("error");
      trackSignupEvent("signup_coupon_failed");
      setCouponMessage("Impossibile verificare il coupon. Riprova tra poco.");
    }
  };

  const applyCouponCode = () => {
    validateCouponCode(signupForm.couponCode, "form");
  };

  useEffect(() => {
    if (!hasInitialCoupon) {
      return;
    }

    validateCouponCode(initialCouponCode, urlCouponCode ? "url" : "automatic");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInitialCoupon, initialCouponCode, urlCouponCode]);

  const buildSignupPayload = () => {
    const trimmedFirstName = signupForm.firstName.trim();
    const trimmedLastName = signupForm.lastName.trim();
    const trimmedCompanyName = signupForm.companyName.trim();

    return {
      firstName: trimmedFirstName,
      lastName: trimmedLastName,
      companyName: trimmedCompanyName,
      email: signupForm.email.trim(),
      phone: signupForm.phone.trim(),
      ...(signupForm.avatarUrl.trim()
        ? { avatarUrl: signupForm.avatarUrl.trim() }
        : {}),
      couponCode:
        couponStatus === "applied"
          ? couponDetails?.code || signupForm.couponCode.trim()
          : "",
      privacyAccepted: signupForm.legalConsent,
      termsAccepted: signupForm.legalConsent,
    };
  };

  const submitSignup = async (event) => {
    event.preventDefault();
    let signupStepName = "check_email";
    const couponApplied = couponStatus === "applied";
    const hasCoupon = Boolean(
      signupForm.couponCode.trim() || couponDetails?.code,
    );
    trackSignupEvent("signup_submit", {
      ...getTrackingAttributionProps(),
      couponApplied,
      hasCoupon,
      couponType: couponApplied ? "applied" : hasCoupon ? "entered" : "none",
    });
    setLoading(true);
    setLoadingLabel("Verifico se l'email è già presente...");
    setSignupError("");
    setSignupMessage("");
    setManagementMessage("");

    try {
      const signupPayload = buildSignupPayload();
      const checkResponse = await fetch(
        `${apiBaseUrl}/self/signup/check-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: signupPayload.email }),
        },
      );
      const checkData = await checkResponse.json();

      if (!checkResponse.ok) {
        trackSignupEvent("signup_check_email_failed", {
          statusCode: checkResponse.status,
        });
        throw new Error(
          checkData.message || "Impossibile verificare questa email.",
        );
      }

      if (checkData.exists || checkData.hasCard) {
        if (eventAvatarHandoff) {
          clearSignupEventAvatarHandoff();
        }
        trackSignupEvent("signup_existing_profile", {
          source: "check_email",
          hasCardUrl: Boolean(checkData.url),
        });
        setSignupStep("existing");
        setIsExistingProfileOpen(true);
        setSignupMessage(
          "Questa email è già collegata a una Cards. Puoi accedere all'area di gestione per aggiornarla.",
        );
        return;
      }

      signupStepName = "create_profile";
      setLoadingLabel("Creo il profilo e preparo la mail di verifica...");
      const createResponse = await fetch(`${apiBaseUrl}/self/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(signupPayload),
      });
      const createData = await createResponse.json();

      if (!createResponse.ok) {
        if (createResponse.status === 409) {
          if (eventAvatarHandoff) {
            clearSignupEventAvatarHandoff();
          }
          trackSignupEvent("signup_existing_profile", {
            source: "create_profile",
            hasCardUrl: Boolean(createData.url),
          });
          setSignupStep("existing");
          setIsExistingProfileOpen(true);
          setSignupMessage(
            "Questa email è già collegata a una Cards. Puoi accedere all'area di gestione per aggiornarla.",
          );
          return;
        }

        trackSignupEvent("signup_create_failed", {
          statusCode: createResponse.status,
        });
        throw new Error(createData.message || "Impossibile creare il profilo.");
      }

      clearStoredSessionToken();
      const signupManageUrl = getSignupManageUrl(
        createData,
        signupPayload.email,
      );
      if (eventAvatarHandoff) {
        clearSignupEventAvatarHandoff();
      }
      setSignupStep("success");
      setSignupSmsPhone(signupPayload.phone);
      setSignupSmsFallbackOpen(false);
      trackSignupEvent("signup_profile_created", {
        couponApplied: couponStatus === "applied",
        hasManageUrl: Boolean(signupManageUrl),
        hasToken: Boolean(getResponseSessionToken(createData)),
        requiresVerification: createData.requiresVerification !== false,
        emailSent: Boolean(createData.emailSent),
      });
      setSignupMessage(
        couponStatus === "applied"
          ? `Profilo creato. Il coupon è stato riscattato: ${normalizeCouponDisplayMessage(
              couponDetails?.publicMessage ||
                "condizioni dedicate applicate alla tua Cards.",
            )} Ti abbiamo inviato una mail con il codice OTP e il link per verificare, finalizzare i dati e attivare la Cards.`
          : "Profilo creato. Ti abbiamo inviato una mail con il codice OTP e il link per verificare, finalizzare i dati e attivare la Cards.",
      );
    } catch (err) {
      trackSignupEvent("signup_error", {
        step: signupStepName,
      });
      setSignupError(err.message);
    } finally {
      setLoading(false);
      setLoadingLabel("");
    }
  };

  const requestManagementCode = async () => {
    setLoading(true);
    setLoadingLabel("Invio codice...");
    setSignupError("");
    setManagementMessage("");
    let failureTracked = false;

    try {
      const response = await fetch(`${apiBaseUrl}/self/request-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: signupForm.email.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        failureTracked = true;
        trackSignupEvent("signup_management_code_failed", {
          statusCode: response.status,
        });
        throw new Error(data.message || "Impossibile inviare il codice.");
      }

      trackSignupEvent("signup_management_code_requested", {
        fromExistingProfile: signupStep === "existing",
      });
      setManagementMessage(
        data.message ||
          "Ti abbiamo inviato un nuovo codice per accedere all'area di gestione.",
      );
    } catch (err) {
      if (!failureTracked) {
        trackSignupEvent("signup_management_code_failed");
      }
      setSignupError(err.message);
    } finally {
      setLoading(false);
      setLoadingLabel("");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const requestSignupSmsVerificationCode = async () => {
    const phone = signupSmsPhone.trim() || signupForm.phone.trim();

    if (!phone) {
      setSignupError("Inserisci il numero di cellulare associato alla Cards.");
      return;
    }

    setSignupSmsLoading(true);
    setSignupError("");
    setSignupMessage("");
    let failureTracked = false;

    try {
      const response = await fetch(`${apiBaseUrl}/self/request-code-sms`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ phone }),
      });

      const data = await response.json();

      if (!response.ok) {
        failureTracked = true;
        trackSignupEvent("signup_sms_code_failed", {
          statusCode: response.status,
        });
        throw new Error(data.message || "Impossibile inviare il codice SMS.");
      }

      trackSignupEvent("signup_sms_code_requested", {
        matchedCards: data.matchedCards || 0,
        smsCount: data.smsCount || 0,
      });

      const signupEmailDebugCode = data.debugCodes?.find(
        (entry) =>
          normalizeEmailValue(entry.email || "") ===
          normalizeEmailValue(signupForm.email),
      );

      if (signupEmailDebugCode?.code) {
        setSignupVerificationCode(signupEmailDebugCode.code);
        setSignupMessage(
          `${data.message || "Codice SMS generato."} Codice test: ${signupEmailDebugCode.code}`,
        );
      } else {
        setSignupMessage(data.message || "Codice inviato via SMS.");
      }
    } catch (err) {
      if (!failureTracked) {
        trackSignupEvent("signup_sms_code_failed");
      }
      setSignupError(err.message);
    } finally {
      setSignupSmsLoading(false);
    }
  };

  const submitSignupVerificationCode = (event) => {
    event.preventDefault();

    if (!signupVerificationCode.trim()) {
      setSignupError("Inserisci il codice di verifica ricevuto via email.");
      return;
    }

    trackSignupEvent("signup_inline_code_submit");
    window.location.href = buildManageCardCodeUrl({
      email: signupForm.email,
      code: signupVerificationCode,
    });
  };

  return (
    <div className="rounded-lg bg-white p-5 shadow-xl shadow-slate-950/10 ring-1 ring-slate-200/70 sm:p-6">
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-cyan-50 text-[#1b8395]">
          <Icon icon="mdi:account-plus-outline" className="text-2xl" />
        </div>
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-[#13526f]">
            Iscrizione gratuita
          </p>
          <h3 className="mt-1 text-2xl font-extrabold text-slate-950">
            Apri il tuo profilo gratuito
          </h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            La registrazione gratuita richiede solo dati personali essenziali e
            nome impresa. Dopo la verifica via email potrai completare la card e
            valutare l'acquisto del Plus.
          </p>
        </div>
      </div>

      {eventAvatarChoices.length > 0 && selectedEventAvatar && (
        <div className="mt-5 rounded-lg bg-slate-950 p-4 text-white ring-1 ring-cyan-200/20">
          <div className="grid gap-4 sm:grid-cols-[104px_1fr] sm:items-center">
            <div className="overflow-hidden rounded-lg bg-slate-900 ring-1 ring-white/10">
              <img
                src={selectedEventAvatar.resultUrl}
                alt="Avatar AI selezionato"
                className="h-32 w-full object-cover sm:h-32"
              />
            </div>
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-cyan-400/15 px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.12em] text-cyan-100 ring-1 ring-cyan-200/20">
                <Icon icon="mdi:auto-fix" className="text-sm" />
                Avatar AI dal totem
              </p>
              <h4 className="mt-3 text-lg font-extrabold leading-tight">
                Lo useremo come foto profilo della tua Cards.
              </h4>
              <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                {eventAvatarChoices.map((avatar) => {
                  const isSelected = avatar.resultUrl === signupForm.avatarUrl;

                  return (
                    <button
                      key={`${avatar.jobId || avatar.resultUrl}-${avatar.themeId}`}
                      type="button"
                      onClick={() => selectEventAvatar(avatar)}
                      className={`relative h-20 w-16 shrink-0 overflow-hidden rounded-md bg-slate-900 ring-2 transition ${
                        isSelected
                          ? "ring-[#e7ff74]"
                          : "ring-white/15 hover:ring-cyan-200/70"
                      }`}
                      aria-label={`Usa ${avatar.label}`}
                    >
                      <img
                        src={avatar.resultUrl}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                      {isSelected && (
                        <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#e7ff74] text-[#073f35]">
                          <Icon icon="mdi:check" className="text-sm" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {signupStep === "success" && (
        <div className="cards-dialog-enter mt-6 rounded-lg bg-emerald-50 p-5 text-emerald-900 ring-1 ring-emerald-200">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-emerald-600 text-white">
              <Icon icon="mdi:check-decagram" className="text-2xl" />
            </div>
            <div>
              <h4 className="text-xl font-extrabold">
                Profilo creato. Verifica la tua email
              </h4>
              <p className="mt-2 text-sm leading-6">{signupMessage}</p>
            </div>
          </div>
          <form
            onSubmit={submitSignupVerificationCode}
            className="mt-5 rounded-lg bg-white p-4 text-slate-900 ring-1 ring-emerald-200"
          >
            <label htmlFor="signup-verification-code" className="block">
              <span className="text-xs font-semibold uppercase tracking-[0.08em] text-emerald-700">
                Codice di verifica ricevuto via email
                <span className="normal-case tracking-normal text-emerald-700/80">
                  {" "}
                  (controlla anche spam o posta indesiderata)
                </span>
              </span>
              <input
                id="signup-verification-code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                value={signupVerificationCode}
                onChange={(event) =>
                  setSignupVerificationCode(event.target.value)
                }
                className="mt-2 w-full rounded-md border border-emerald-200 px-4 py-3 text-center text-2xl font-bold tracking-[0.2em] text-slate-900 outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                maxLength={6}
                placeholder="000000"
              />
            </label>
            {/* SMS OTP fallback disabilitato temporaneamente — verrà riattivato in futuro.
            <div className="mt-3 rounded-lg bg-emerald-50 p-3 ring-1 ring-emerald-100">
              <button
                type="button"
                onClick={() => setSignupSmsFallbackOpen((current) => !current)}
                className="inline-flex w-full items-center justify-between gap-3 text-left text-sm font-bold text-emerald-800"
                aria-expanded={signupSmsFallbackOpen}
              >
                <span>OTP non ricevuto? Prova con SMS</span>
                <Icon
                  icon={
                    signupSmsFallbackOpen
                      ? "mdi:chevron-up"
                      : "mdi:chevron-down"
                  }
                  className="text-xl"
                />
              </button>
              {signupSmsFallbackOpen && (
                <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                  <label className="block">
                    <span className="sr-only">
                      Numero cellulare per OTP SMS
                    </span>
                    <input
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={signupSmsPhone}
                      onChange={(event) =>
                        setSignupSmsPhone(event.target.value)
                      }
                      className="w-full rounded-md border border-emerald-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                      placeholder="+39 333 1234567"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={requestSignupSmsVerificationCode}
                    disabled={signupSmsLoading || !apiBaseUrl}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-white px-4 py-3 text-sm font-bold text-emerald-800 ring-1 ring-emerald-200 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Icon icon="mdi:message-lock-outline" className="text-lg" />
                    {signupSmsLoading ? "Invio..." : "Invia SMS"}
                  </button>
                </div>
              )}
            </div>
            */}
            <button
              type="submit"
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md bg-emerald-600 px-4 py-3 text-sm font-extrabold text-white transition hover:bg-emerald-700"
            >
              <Icon icon="mdi:lock-open-check" className="text-lg" />
              Verifica e modifica i dati
            </button>
          </form>
        </div>
      )}

      {signupStep === "existing" && (
        <div className="mt-6 rounded-lg bg-amber-50 p-5 text-amber-950 ring-1 ring-amber-200">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-amber-500 text-white">
              <Icon icon="mdi:account-alert-outline" className="text-2xl" />
            </div>
            <div>
              <h4 className="text-xl font-extrabold">Profilo già presente</h4>
              <p className="mt-2 text-sm leading-6">
                L'email inserita è già associata a una Cards. Non apriamo un
                nuovo profilo: accedi all'area di gestione per modificare la
                card esistente o richiedi un nuovo codice di accesso.
              </p>
            </div>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <a
              href={buildManageCardUrl({ email: signupForm.email })}
              onClick={() =>
                trackSignupEvent("signup_existing_profile_manage_click", {
                  source: "existing_panel",
                })
              }
              className="inline-flex items-center justify-center gap-2 rounded-md bg-[#1b8395] px-4 py-3 text-sm font-extrabold text-white transition hover:bg-[#13526f]"
            >
              <Icon icon="mdi:login-variant" className="text-lg" />
              Gestisci la card
            </a>
            <button
              type="button"
              onClick={requestManagementCode}
              disabled={loading || !apiBaseUrl}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white px-4 py-3 text-sm font-extrabold text-amber-900 ring-1 ring-amber-200 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Icon icon="mdi:email-fast" className="text-lg" />
              {loading ? "Invio..." : "Ricevi codice"}
            </button>
          </div>
          {managementMessage && (
            <div className="mt-4 rounded-md bg-white px-4 py-3 text-sm font-bold leading-6 text-amber-900 ring-1 ring-amber-200">
              {managementMessage}
            </div>
          )}
        </div>
      )}

      {signupStep === "form" && (
        <form onSubmit={submitSignup}>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {signupFields.map(([id, name, label, type, placeholder]) => (
              <label key={id} htmlFor={id} className="block">
                <span className="text-xs font-semibold text-slate-500">
                  {label}
                </span>
                <input
                  id={id}
                  name={name}
                  type={type}
                  required
                  placeholder={placeholder}
                  value={signupForm[name]}
                  onChange={(event) =>
                    updateSignupField(name, event.target.value)
                  }
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-cyan-600 focus:ring-2 focus:ring-cyan-100"
                />
              </label>
            ))}
          </div>

          {hasInitialCoupon && couponStatus === "applied" && (
            <div className="mt-5 rounded-lg bg-[#073f35] p-4 text-white shadow-lg shadow-emerald-900/10">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#e7ff74] text-[#073f35]">
                  <Icon icon="mdi:gift-open-outline" className="text-2xl" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-extrabold text-[#e7ff74]">
                    Omaggio fiera già applicato
                  </p>
                  <p className="mt-1 break-words text-sm leading-6 text-emerald-50">
                    Completa il form per attivare Cards Plus gratuito:{" "}
                    {couponMessage}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-5 rounded-lg border border-cyan-100 bg-cyan-50/70 p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white text-[#1b8395] shadow-sm">
                <Icon icon="mdi:ticket-percent-outline" className="text-xl" />
              </div>
              <div>
                <p className="text-sm font-extrabold text-slate-950">
                  Hai un coupon?
                </p>
                <p className="mt-1 text-xs leading-5 text-slate-600">
                  Inseriscilo prima di aprire il profilo per riscattare le
                  condizioni associate al codice.
                </p>
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto]">
              <label htmlFor="signup-coupon-code" className="block">
                <span className="sr-only">Codice coupon</span>
                <input
                  id="signup-coupon-code"
                  name="couponCode"
                  type="text"
                  placeholder="Codice coupon"
                  value={signupForm.couponCode}
                  onChange={(event) =>
                    updateSignupField(
                      "couponCode",
                      event.target.value.toUpperCase(),
                    )
                  }
                  className="w-full rounded-md border border-cyan-200 bg-white px-3 py-3 text-sm font-bold uppercase tracking-[0.08em] text-slate-900 outline-none transition placeholder:normal-case placeholder:tracking-normal focus:border-cyan-600 focus:ring-2 focus:ring-cyan-100"
                />
              </label>
              <button
                type="button"
                onClick={applyCouponCode}
                disabled={
                  couponStatus === "loading" || couponStatus === "applied"
                }
                className={`inline-flex min-w-[150px] items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-extrabold text-white shadow-sm transition disabled:cursor-not-allowed sm:w-auto ${
                  couponStatus === "applied"
                    ? "bg-emerald-600 hover:bg-emerald-600 disabled:opacity-100"
                    : "bg-[#1b8395] hover:bg-[#13526f] disabled:opacity-70"
                }`}
              >
                <Icon
                  icon={
                    couponStatus === "applied"
                      ? "mdi:check-circle"
                      : "mdi:ticket-confirmation-outline"
                  }
                  className="text-lg"
                />
                {couponStatus === "loading"
                  ? "Verifica..."
                  : couponStatus === "applied"
                    ? "Applicato"
                    : "Applica"}
              </button>
            </div>

            {couponMessage && (
              <div
                className={`mt-3 rounded-md px-3 py-3 text-sm font-bold leading-6 ${
                  couponStatus === "applied"
                    ? "bg-emerald-100 text-emerald-800"
                    : couponStatus === "loading"
                      ? "bg-cyan-100 text-[#13526f]"
                      : "bg-red-50 text-red-700"
                }`}
              >
                {couponMessage}
              </div>
            )}
          </div>

          <label className="mt-5 flex items-start gap-3 rounded-md border border-slate-200 px-3 py-3 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              required
              checked={signupForm.legalConsent}
              onChange={(event) =>
                updateSignupField("legalConsent", event.target.checked)
              }
              className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1b8395] focus:ring-cyan-600"
            />
            <span>
              Accetto la{" "}
              <a
                href={privacyPolicyUrl}
                target="_blank"
                rel="noreferrer"
                className="font-bold text-[#13526f] underline decoration-cyan-500/40 underline-offset-2"
              >
                Privacy Policy
              </a>{" "}
              e i{" "}
              <a
                href={termsUrl}
                target="_blank"
                rel="noreferrer"
                className="font-bold text-[#13526f] underline decoration-cyan-500/40 underline-offset-2"
              >
                Termini e condizioni
              </a>
            </span>
          </label>

          {loading && loadingLabel && (
            <div
              role="status"
              aria-live="polite"
              className="mt-5 flex items-center gap-3 rounded-lg border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm font-bold text-[#13526f] shadow-sm"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-[#1b8395] shadow-sm">
                <Icon icon="mdi:loading" className="text-xl animate-spin" />
              </span>
              <span>
                <span className="block text-slate-950">
                  {signupLoadingPhrases[loadingPhraseIndex]}
                </span>
                <span className="mt-0.5 block text-xs font-semibold text-[#13526f]">
                  {loadingLabel}
                </span>
              </span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !apiBaseUrl || couponStatus === "loading"}
            aria-busy={loading}
            className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-extrabold text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${
              couponStatus === "applied"
                ? "bg-emerald-600 hover:bg-emerald-700"
                : "bg-[#1b8395] hover:bg-[#13526f]"
            }`}
          >
            <Icon
              icon={
                loading
                  ? "mdi:loading"
                  : couponStatus === "applied"
                    ? "mdi:gift-open-outline"
                    : "mdi:account-check-outline"
              }
              className={`text-lg ${loading ? "animate-spin" : ""}`}
            />
            {loading
              ? "Verifica in corso..."
              : couponStatus === "applied"
                ? "Apri il profilo e riscatta il coupon"
                : "Apri profilo gratis"}
          </button>
        </form>
      )}

      {(signupMessage || signupError) && signupStep !== "success" && (
        <div
          className={`mt-4 rounded-md px-4 py-3 text-sm font-medium leading-6 ${
            signupError
              ? "bg-red-50 text-red-700"
              : "bg-emerald-50 text-emerald-700"
          }`}
        >
          {signupError || signupMessage}
        </div>
      )}

      {isExistingProfileOpen && (
        <div className="cards-modal-backdrop fixed inset-0 z-50 flex items-end justify-center bg-slate-950/60 px-3 pb-0 pt-8 backdrop-blur-sm md:items-center md:p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="existing-profile-title"
            className="cards-otp-dialog w-full max-w-md rounded-t-xl bg-white p-4 shadow-2xl shadow-slate-950/30 ring-1 ring-slate-200 md:rounded-lg md:p-6"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-amber-50 text-amber-600">
                <Icon icon="mdi:account-alert-outline" className="text-2xl" />
              </div>
              <div className="min-w-0 flex-1">
                <h3
                  id="existing-profile-title"
                  className="text-lg font-extrabold text-slate-950"
                >
                  Profilo già presente
                </h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Questa email è già collegata a una Cards. Per sicurezza non
                  creiamo un secondo profilo: usa l'area di gestione per
                  aggiornare la card esistente.
                </p>
              </div>
            </div>

            <div className="mt-6 grid gap-3 md:grid-cols-2">
              <button
                type="button"
                onClick={() => setIsExistingProfileOpen(false)}
                className="rounded-md border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Chiudi
              </button>
              <a
                href={buildManageCardUrl({ email: signupForm.email })}
                onClick={() =>
                  trackSignupEvent("signup_existing_profile_manage_click", {
                    source: "existing_modal",
                  })
                }
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#1b8395] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#13526f]"
              >
                <Icon icon="mdi:login-variant" className="text-lg" />
                Gestisci card
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UrlCouponBanner() {
  const apiBaseUrl = useMemo(getApiBaseUrl, []);
  const couponCode = useMemo(getInitialCouponCode, []);
  const isTanexpoCoupon = couponCode === fairCouponCode;
  const [couponState, setCouponState] = useState({
    status: couponCode ? "loading" : "idle",
    coupon: null,
    message: "",
  });
  const couponExpiresAt = couponState.coupon?.expiresAt || "";
  const couponDeadlineLabel = formatCouponDeadlineLabel(couponExpiresAt);
  const [countdown, setCountdown] = useState(() =>
    getCouponCountdown(couponExpiresAt),
  );

  useEffect(() => {
    if (!couponCode || !apiBaseUrl) {
      return;
    }

    let isMounted = true;

    const validate = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/self/coupon/validate`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ couponCode }),
        });
        const data = await response.json();

        if (!isMounted) {
          return;
        }

        if (response.ok && data.valid && data.coupon) {
          setCouponState({
            status: "valid",
            coupon: data.coupon,
            message:
              data.message ||
              data.coupon.publicMessage ||
              "Completa l'iscrizione per riscattare le condizioni associate al codice.",
          });
          return;
        }

        setCouponState({
          status: "invalid",
          coupon: null,
          message: data.message || "Codice coupon non valido.",
        });
      } catch (err) {
        if (isMounted) {
          setCouponState({
            status: "invalid",
            coupon: null,
            message: "Impossibile verificare il coupon.",
          });
        }
      }
    };

    validate();

    return () => {
      isMounted = false;
    };
  }, [apiBaseUrl, couponCode]);

  useEffect(() => {
    if (!couponExpiresAt) {
      setCountdown(null);
      return undefined;
    }

    setCountdown(getCouponCountdown(couponExpiresAt));
    const intervalId = window.setInterval(() => {
      setCountdown(getCouponCountdown(couponExpiresAt));
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [couponExpiresAt]);

  if (!couponCode) {
    return null;
  }

  const couponMessage =
    couponState.status === "valid"
      ? normalizeCouponDisplayMessage(
          couponState.message ||
            "Apri il profilo e ottieni Cards Plus gratuito, omaggio offerto da Onoranzefunebricloud e Annuncifunebri.",
        )
      : couponState.message || "Completa l'iscrizione per riscattare l'omaggio fiera.";
  const headline = isTanexpoCoupon
    ? "Per te Cards Plus gratuito"
    : "Coupon omaggio attivo sulla tua Cards";

  return (
    <section className="bg-[#073f35] px-5 py-7 text-white sm:py-8">
      <div className="mx-auto grid max-w-6xl gap-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
        <div className="min-w-0 flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-[#e7ff74] text-[#073f35] shadow-lg shadow-black/10">
            <Icon icon="mdi:gift-open-outline" className="text-3xl" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="inline-flex rounded-full bg-white/12 px-3 py-1 text-xs font-extrabold uppercase tracking-[0.14em] text-[#e7ff74] ring-1 ring-white/20">
              Omaggio fiera
            </p>
            <h2 className="mt-3 max-w-3xl text-2xl font-extrabold leading-tight sm:text-5xl">
              {headline}
            </h2>
            <p className="mt-3 max-w-2xl break-words text-sm font-semibold leading-7 text-emerald-50 sm:text-base">
              Il coupon{" "}
              <span className="font-extrabold text-[#e7ff74]">
                {couponCode}
              </span>{" "}
              è già pronto: {couponMessage}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="text-xs font-extrabold uppercase tracking-[0.14em] text-emerald-100">
                Offerto da:
              </span>
              <span className="rounded-md bg-white px-3 py-2">
                <img
                  src={ofcLogoUrl}
                  alt="Onoranzefunebricloud"
                  className="h-8 w-auto"
                />
              </span>
              <span className="rounded-md bg-white px-3 py-2">
                <img
                  src={annunciFunebriFairLogoUrl}
                  alt="Annuncifunebri"
                  className="h-8 w-auto"
                />
              </span>
            </div>
          </div>
        </div>
        <div className="min-w-0 flex flex-col gap-3 md:items-end">
          <div className="w-full rounded-lg bg-white/10 p-3 ring-1 ring-white/15 md:w-[320px]">
            {couponState.status === "loading" ? (
              <p className="text-center text-sm font-extrabold text-[#e7ff74]">
                Verifico validità coupon...
              </p>
            ) : couponState.status === "invalid" ? (
              <p className="text-center text-sm font-extrabold text-[#e7ff74]">
                {couponState.message}
              </p>
            ) : !couponDeadlineLabel || !countdown ? (
              <p className="text-center text-sm font-extrabold text-[#e7ff74]">
                Coupon attivo
              </p>
            ) : countdown.expired ? (
              <p className="text-center text-sm font-extrabold text-[#e7ff74]">
                Promo scaduta il {couponDeadlineLabel}
              </p>
            ) : (
              <>
                <p className="mb-2 text-center text-xs font-extrabold uppercase tracking-[0.14em] text-emerald-100">
                  Tempo residuo omaggio
                </p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[
                    ["Giorni", countdown.days],
                    ["Ore", countdown.hours],
                    ["Min", countdown.minutes],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="rounded-md bg-white text-[#073f35] p-2"
                    >
                      <span className="block text-2xl font-black leading-none">
                        {String(value).padStart(2, "0")}
                      </span>
                      <span className="mt-1 block text-[10px] font-extrabold uppercase tracking-[0.08em]">
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-center text-xs font-semibold text-emerald-100">
                  Scade il {couponDeadlineLabel}
                </p>
              </>
            )}
          </div>
          <a
            href="#iscriviti"
            data-track-home-event="home_cta_click"
            data-track-label="Riscatta coupon fiera"
            data-track-source="coupon_banner"
            data-track-target="#iscriviti"
            className="inline-flex w-full min-w-0 items-center justify-center gap-2 rounded-md bg-[#e7ff74] px-4 py-3 text-sm font-extrabold text-[#073f35] shadow-lg shadow-black/15 transition hover:bg-white md:w-auto md:px-5"
          >
            <Icon icon="mdi:ticket-confirmation-outline" className="text-xl" />
            <span className="min-w-0 text-center leading-5">
              Riscatta coupon e attiva la tua card
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}

function HomePage() {
  const initialCouponCode = useMemo(getInitialCouponCode, []);
  const urlCouponCode = useMemo(getUrlCouponCode, []);
  const hasInitialCoupon = Boolean(initialCouponCode);
  const trackHomeClick = useCallback((event) => {
    const target = event.target.closest?.("[data-track-home-event]");

    if (!target || !event.currentTarget.contains(target)) {
      return;
    }

    trackHomeEvent(target.dataset.trackHomeEvent, {
      label: target.dataset.trackLabel,
      source: target.dataset.trackSource,
      target: target.dataset.trackTarget,
    });
  }, []);

  useEffect(() => {
    const pathname =
      typeof window === "undefined" ? "/" : window.location.pathname;
    setPageSeo(pathname === "/" ? homeSeo : noIndexSeo);
  }, []);

  useEffect(() => {
    trackHomeEvent("home_viewed", {
      ...getTrackingAttributionProps(),
      couponApplied: hasInitialCoupon,
      hasCoupon: hasInitialCoupon,
      couponType: hasInitialCoupon
        ? urlCouponCode
          ? "url"
          : "automatic"
        : "none",
    });
  }, [hasInitialCoupon, urlCouponCode]);

  return (
    <div
      onClick={trackHomeClick}
      className="min-h-screen bg-[#e8eef2] font-Poppins text-slate-950"
    >
      <header className="sticky top-0 z-40 bg-[#17465c]/95 text-white backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <a
            href="/"
            data-track-home-event="home_brand_click"
            data-track-label="Cards"
            data-track-source="header"
            data-track-target="/"
            className="flex items-center"
          >
            <img src={cardsWhiteLogoUrl} alt="Cards" className="h-9 w-auto" />
          </a>
          <nav className="hidden items-center gap-7 text-xs font-semibold text-cyan-50/80 md:flex">
            <a
              href="#come-funziona"
              data-track-home-event="home_nav_click"
              data-track-label="Come funziona"
              data-track-source="header"
              data-track-target="#come-funziona"
              className="hover:text-white"
            >
              Come funziona
            </a>
            <a
              href="#piani"
              data-track-home-event="home_nav_click"
              data-track-label="Piani"
              data-track-source="header"
              data-track-target="#piani"
              className="hover:text-white"
            >
              Piani
            </a>
            <a
              href="#faq"
              data-track-home-event="home_nav_click"
              data-track-label="FAQ"
              data-track-source="header"
              data-track-target="#faq"
              className="hover:text-white"
            >
              FAQ
            </a>
          </nav>
          <div className="flex items-center gap-2">
            <a
              href="/gestisci"
              data-track-home-event="home_cta_click"
              data-track-label="Gestisci card"
              data-track-source="header"
              data-track-target="/gestisci"
              className="hidden rounded-md bg-white/10 px-4 py-2 text-xs font-bold text-white ring-1 ring-white/20 transition hover:bg-white/15 sm:inline-flex"
            >
              Gestisci card
            </a>
            <a
              href="#iscriviti"
              data-track-home-event="home_cta_click"
              data-track-label="Apri profilo"
              data-track-source="header"
              data-track-target="#iscriviti"
              className="rounded-md bg-white px-4 py-2 text-xs font-extrabold text-[#13526f] shadow-sm transition hover:bg-cyan-50"
            >
              Apri profilo
            </a>
          </div>
        </div>
      </header>

      <main>
        <UrlCouponBanner />

        <section className="relative overflow-hidden bg-gradient-to-br from-[#164b66] via-[#1b6f84] to-[#2399a4] text-white">
          <div className="absolute inset-x-0 bottom-[-1px] h-16 rounded-t-[55%] bg-[#e8eef2]" />
          <div className="relative mx-auto grid max-w-6xl gap-10 px-5 pb-28 pt-14 lg:grid-cols-[1fr_0.9fr] lg:items-center">
            <div>
              <span className="rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold text-cyan-50 ring-1 ring-white/15">
                Servizio digitale dedicato e powered by OnoranzeFunebriCloud
              </span>
              <h1 className="mt-5 max-w-2xl text-4xl font-extrabold leading-[1.03] sm:text-5xl">
                Il tuo biglietto da visita, sempre pronto da condividere.
              </h1>
              <img
                src={heroMockupUrl}
                alt="Mockup Cards su smartphone"
                className="mx-auto mt-7 w-full max-w-[360px] rounded-lg drop-shadow-2xl lg:hidden"
              />
              <p className="mt-5 max-w-2xl text-base font-medium leading-7 text-cyan-50/90">
                Cards è il biglietto da visita digitale pensato per agenzie
                funebri, case funerarie, centri servizi, crematori e fornitori
                del settore. Un link semplice da inviare via WhatsApp, email o
                QR code, con i tuoi dati sempre aggiornati e pronti all'uso.
              </p>

              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                {[
                  [
                    "mdi:share-variant",
                    "Condivisione immediata durante appuntamenti, eventi, fiere e visite commerciali.",
                  ],
                  [
                    "mdi:shield-star-outline",
                    "Più immagine, più ordine, più fiducia nei tuoi contatti.",
                  ],
                  [
                    "mdi:briefcase-clock-outline",
                    "Dati utili sempre a portata di mano, anche quando si è in movimento.",
                  ],
                  [
                    "mdi:card-account-details-star-outline",
                    "Uno strumento digitale più professionale di un semplice segnaposto.",
                  ],
                ].map(([icon, item]) => (
                  <div
                    key={item}
                    className="flex gap-3 rounded-lg bg-white/10 p-4 text-sm font-medium leading-6 text-cyan-50 ring-1 ring-white/10"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white/15 text-white">
                      <Icon icon={icon} className="text-lg" />
                    </span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href="#iscriviti"
                  data-track-home-event="home_cta_click"
                  data-track-label="Apri profilo gratis"
                  data-track-source="hero"
                  data-track-target="#iscriviti"
                  className="inline-flex items-center gap-2 rounded-md bg-white px-5 py-3 text-sm font-extrabold text-[#13526f] shadow-lg shadow-slate-950/15 transition hover:bg-cyan-50"
                >
                  <Icon icon="mdi:account-plus-outline" className="text-lg" />
                  Apri profilo gratis
                </a>
                <a
                  href="#piani"
                  data-track-home-event="home_cta_click"
                  data-track-label="Scopri i piani"
                  data-track-source="hero"
                  data-track-target="#piani"
                  className="rounded-md bg-white/10 px-5 py-3 text-sm font-bold text-white ring-1 ring-white/20 transition hover:bg-white/15"
                >
                  Scopri i piani
                </a>
              </div>

              <p className="mt-5 text-xs leading-5 text-cyan-50/80">
                Si parte sempre dal profilo gratuito. Il piano Plus da 49€/anno
                si può acquistare dopo, direttamente dall'area profilo.
              </p>
            </div>

            <div className="relative mx-auto hidden w-full max-w-[500px] lg:block">
              <div className="absolute -left-5 top-8 z-10 hidden items-center gap-2 rounded-lg bg-white px-4 py-3 text-xs font-extrabold text-slate-700 shadow-xl ring-1 ring-slate-200/70 lg:flex">
                <Icon
                  icon="mdi:qrcode-scan"
                  className="text-lg text-[#1b8395]"
                />
                QR pronto da condividere
              </div>
              <div className="absolute -right-6 top-1/2 z-10 hidden -translate-y-1/2 items-center gap-2 rounded-lg bg-white px-4 py-3 text-xs font-extrabold text-slate-700 shadow-xl ring-1 ring-slate-200/70 lg:flex">
                <Icon
                  icon="mdi:account-plus"
                  className="text-lg text-[#1b8395]"
                />
                Salvataggio contatto rapido
              </div>
              <div className="absolute -bottom-4 left-8 z-10 hidden items-center gap-2 rounded-lg bg-white px-4 py-3 text-xs font-extrabold text-slate-700 shadow-xl ring-1 ring-slate-200/70 lg:flex">
                <Icon
                  icon="mdi:sync-circle"
                  className="text-lg text-[#1b8395]"
                />
                Dati sempre aggiornati
              </div>
              <img
                src={heroMockupUrl}
                alt="Mockup Cards su smartphone"
                className="w-full rounded-lg drop-shadow-2xl"
              />
            </div>
          </div>
        </section>

        <section id="come-funziona" className="px-5 py-20">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
              <div>
                <SectionLabel>Come funziona</SectionLabel>
                <h2 className="mt-4 max-w-2xl text-3xl font-extrabold leading-tight sm:text-4xl">
                  Una pagina pulita, mobile-first, pronta da condividere e
                  semplice da ricordare.
                </h2>
                <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-600">
                  Cards è progettata per essere chiara in pochi secondi: nome,
                  ruolo, contatti essenziali, azioni rapide e, nel piano Plus,
                  tutte le informazioni aziendali più utili. Dalla stessa area
                  puoi anche preparare materiali operativi come QR scaricabile e
                  firma email HTML, così la card resta coerente anche fuori dal
                  link pubblico. È un piccolo strumento che rende più semplice
                  farsi trovare, ricordare e ricontattare.
                </p>
              </div>
              <img
                src={howItWorksImageUrl}
                alt="Illustrazione del funzionamento di Cards"
                className="w-full"
              />
            </div>
            <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {featureCards.map((card) => (
                <article
                  key={card.title}
                  className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-slate-200/70"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-cyan-50 text-[#1b8395]">
                    <Icon icon={card.icon} className="text-xl" />
                  </div>
                  <h3 className="mt-4 text-base font-extrabold">
                    {card.title}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    {card.text}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <InteractiveProductMockups />

        <section className="px-5 pb-20">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
              <img
                src={audienceImageUrl}
                alt="Cards pensata per il contesto della filiera funebre"
                className="order-2 w-full lg:order-1"
              />
              <div className="order-1 lg:order-2">
                <SectionLabel>Pensata per il tuo contesto</SectionLabel>
                <h2 className="mt-4 max-w-3xl text-3xl font-extrabold leading-tight sm:text-4xl">
                  Benefici concreti per onoranze funebri e fornitori del
                  settore.
                </h2>
                <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-600">
                  Non è un semplice profilo online: è uno strumento operativo e
                  commerciale che semplifica i contatti nelle situazioni reali
                  di tutti i giorni.
                </p>
              </div>
            </div>
            <div className="mt-10 grid gap-5 md:grid-cols-2">
              {audienceCards.map((card) => (
                <article
                  key={card.title}
                  className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-slate-200/70"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-cyan-50 text-[#1b8395]">
                      <Icon icon={card.icon} className="text-xl" />
                    </div>
                    <div>
                      <h3 className="font-extrabold">{card.title}</h3>
                      <ul className="mt-3 space-y-2 text-sm leading-6 text-slate-600">
                        {card.items.map((item) => (
                          <li key={item} className="flex gap-2">
                            <Icon
                              icon="mdi:check"
                              className="mt-1 shrink-0 text-[#20acad]"
                            />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="piani" className="px-5 pb-20">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
              <div>
                <SectionLabel>Piani</SectionLabel>
                <h2 className="mt-4 max-w-2xl text-3xl font-extrabold leading-tight sm:text-4xl">
                  Parti gratis oppure sblocca il massimo con Plus.
                </h2>
                <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-600">
                  Una struttura semplice: prima apri il profilo gratuito con i
                  dati essenziali e il nome impresa, poi dall'interno del
                  profilo puoi decidere se acquistare il piano Plus annuale.
                </p>
              </div>
              <img
                src={plansImageUrl}
                alt="Illustrazione dei piani Cards gratuito e Plus"
                className="w-full"
              />
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {[
                [
                  "Ingresso semplice",
                  "Il piano gratuito permette di iniziare subito e testare il servizio in modo semplice.",
                ],
                [
                  "Uso professionale",
                  "Il Plus aggiunge branding, dati aziendali completi, firma email HTML e Avatar AI a crediti.",
                ],
                [
                  "Evoluzione graduale",
                  "Cards è pensata per crescere con nuove funzioni dedicate alla filiera.",
                ],
              ].map(([title, text]) => (
                <div
                  key={title}
                  className="rounded-lg bg-white p-4 text-sm shadow-sm ring-1 ring-slate-200/70"
                >
                  <h3 className="font-extrabold text-[#13526f]">{title}</h3>
                  <p className="mt-2 leading-6 text-slate-600">{text}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-xl bg-white p-5 shadow-sm ring-1 ring-cyan-100 md:p-6">
              <div className="grid gap-5 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
                <div>
                  <p className="inline-flex items-center gap-2 rounded-full bg-cyan-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-cyan-700 ring-1 ring-cyan-100">
                    <Icon icon="mdi:email-edit-outline" className="text-sm" />
                    Plus
                  </p>
                  <h3 className="mt-3 text-2xl font-extrabold leading-tight text-slate-950">
                    Firma email coordinata con la tua Cards.
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-slate-600">
                    Il generatore firma usa i dati già salvati nel profilo:
                    nome, ruolo, azienda, contatti, logo, foto, social e QR
                    code. Scegli il template, nascondi le informazioni che non
                    vuoi mostrare e copia una firma pronta per i principali
                    client email.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    [
                      "Template multipli",
                      "Stili minimal, corporate, foto profilo, CTA e QR in evidenza.",
                    ],
                    [
                      "Campi controllabili",
                      "Mostri solo telefono, email, sito, social, indirizzo o QR quando servono.",
                    ],
                    [
                      "HTML e testo",
                      "Copia firma HTML, codice sorgente oppure versione solo testo.",
                    ],
                    [
                      "Coerenza brand",
                      "Colore principale e asset della card restano allineati al profilo pubblico.",
                    ],
                  ].map(([title, text]) => (
                    <div
                      key={title}
                      className="rounded-lg bg-slate-50 p-4 ring-1 ring-slate-200"
                    >
                      <h4 className="text-sm font-extrabold text-[#13526f]">
                        {title}
                      </h4>
                      <p className="mt-2 text-xs font-medium leading-5 text-slate-600">
                        {text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-10">
              <h3 className="text-2xl font-extrabold leading-tight text-slate-950">
                Gratis vs Plus
              </h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">
                Una tabella semplice per capire subito cosa cambia tra i due
                piani.
              </p>
              <div className="mt-6 grid gap-4 md:hidden">
                {[
                  {
                    title: "Gratis",
                    cta: "Inizia subito",
                    ctaClass:
                      "border border-slate-200 bg-white text-slate-800 hover:bg-slate-100",
                    values: comparisonRows.map(([label, free]) => [
                      label,
                      free,
                    ]),
                  },
                  {
                    title: "Plus",
                    cta: "Apri il profilo",
                    ctaClass: "bg-[#1b8395] text-white hover:bg-[#13526f]",
                    values: comparisonRows.map(([label, , plus]) => [
                      label,
                      plus,
                    ]),
                  },
                ].map((plan) => (
                  <article
                    key={plan.title}
                    className="rounded-lg bg-white p-5 shadow-sm ring-1 ring-slate-200/70"
                  >
                    <h4 className="text-xl font-extrabold text-slate-950">
                      {plan.title}
                    </h4>
                    <dl className="mt-4 divide-y divide-slate-100">
                      {plan.values.map(([label, value]) => (
                        <div
                          key={`${plan.title}-${label}`}
                          className="flex items-center justify-between gap-4 py-3 text-sm"
                        >
                          <dt className="font-medium text-slate-600">
                            {label}
                          </dt>
                          <dd className="shrink-0 text-right font-extrabold text-slate-900">
                            {value === true && (
                              <Icon
                                icon="mdi:check"
                                className="text-lg text-[#1b8395]"
                              />
                            )}
                            {value === false && (
                              <span className="text-slate-300">-</span>
                            )}
                            {value === "coming" && (
                              <span className="text-xs text-slate-500">
                                Coming soon
                              </span>
                            )}
                            {typeof value === "string" &&
                              value !== "coming" &&
                              value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                    <a
                      href="#iscriviti"
                      data-track-home-event="home_cta_click"
                      data-track-label={plan.cta}
                      data-track-source={`pricing_mobile_${plan.title.toLowerCase()}`}
                      data-track-target="#iscriviti"
                      className={`mt-5 inline-flex w-full items-center justify-center rounded-md px-4 py-3 text-sm font-extrabold transition ${plan.ctaClass}`}
                    >
                      {plan.cta}
                    </a>
                  </article>
                ))}
              </div>
              <div className="mt-6 hidden overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-slate-200/70 md:block">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-[0.08em] text-[#13526f]">
                    <tr>
                      <th className="px-5 py-4">Funzionalità</th>
                      <th className="px-5 py-4 text-center">Gratis</th>
                      <th className="px-5 py-4 text-center">Plus</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {comparisonRows.map(([label, free, plus]) => (
                      <tr key={label}>
                        <td className="px-5 py-4 font-medium text-slate-700">
                          {label}
                        </td>
                        {[free, plus].map((value, index) => (
                          <td
                            key={`${label}-${index}`}
                            className="px-5 py-4 text-center"
                          >
                            {value === true && (
                              <Icon
                                icon="mdi:check"
                                className="mx-auto text-lg text-[#1b8395]"
                              />
                            )}
                            {typeof value === "string" &&
                              value !== "coming" && (
                                <span className="text-sm font-extrabold text-slate-800">
                                  {value}
                                </span>
                              )}
                            {value === false && (
                              <span className="text-slate-300">-</span>
                            )}
                            {value === "coming" && (
                              <span className="text-xs font-bold text-slate-500">
                                Coming soon
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="bg-slate-50">
                      <td className="px-5 py-4 font-bold text-slate-800">
                        Azione
                      </td>
                      <td className="px-5 py-4 text-center">
                        <a
                          href="#iscriviti"
                          data-track-home-event="home_cta_click"
                          data-track-label="Inizia subito"
                          data-track-source="pricing_table_free"
                          data-track-target="#iscriviti"
                          className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white px-4 py-2 text-xs font-extrabold text-slate-800 transition hover:bg-slate-100"
                        >
                          Inizia subito
                        </a>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <a
                          href="#iscriviti"
                          data-track-home-event="home_cta_click"
                          data-track-label="Apri il profilo"
                          data-track-source="pricing_table_plus"
                          data-track-target="#iscriviti"
                          className="inline-flex items-center justify-center rounded-md bg-[#1b8395] px-4 py-2 text-xs font-extrabold text-white transition hover:bg-[#13526f]"
                        >
                          Apri il profilo
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs font-medium leading-5 text-slate-500">
                * Il generatore Avatar AI è disponibile nel piano Plus e
                funziona tramite crediti.
              </p>
            </div>
          </div>
        </section>

        <section id="iscriviti" className="px-5 pb-20">
          <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[0.82fr_1.18fr] lg:items-start">
            <div>
              <img
                src={signupImageUrl}
                alt="Illustrazione iscrizione Cards"
                className="mb-5 w-full max-w-[220px]"
              />
              <SectionLabel>Iscrizione</SectionLabel>
              <h2 className="mt-4 max-w-2xl text-3xl font-extrabold leading-tight sm:text-4xl">
                Il primo passo è sempre il profilo gratuito.
              </h2>
              <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-600">
                Per iniziare non devi scegliere subito un piano a pagamento:
                apri il profilo gratuito con email, nome, cognome, nome impresa,
                telefono e accettazione di{" "}
                <a
                  href={privacyPolicyUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-bold text-[#13526f] underline decoration-cyan-500/40 underline-offset-2"
                >
                  Privacy Policy
                </a>{" "}
                e{" "}
                <a
                  href={termsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-bold text-[#13526f] underline decoration-cyan-500/40 underline-offset-2"
                >
                  Termini e condizioni
                </a>
                .
              </p>
              <div className="mt-6 space-y-3">
                {[
                  "Il profilo gratuito crea la base della tua Cards.",
                  "Dall'area profilo puoi completare o aggiornare i dati della card.",
                  "Il Plus si acquista solo dopo, quando decidi di usare funzioni più complete.",
                ].map((item) => (
                  <div
                    key={item}
                    className="flex gap-3 rounded-lg bg-white p-4 text-sm font-medium leading-6 text-slate-700 shadow-sm ring-1 ring-slate-200/70"
                  >
                    <Icon
                      icon="mdi:check-circle"
                      className="mt-0.5 shrink-0 text-lg text-[#20acad]"
                    />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <SignupForm />
          </div>
        </section>

        <section id="gestisci" className="px-5 pb-20">
          <div className="mx-auto grid max-w-6xl gap-6 rounded-lg bg-gradient-to-br from-[#164b66] to-[#2298a4] p-6 text-white shadow-2xl shadow-slate-950/15 sm:p-10 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <SectionLabel>Area personale</SectionLabel>
              <h2 className="mt-4 text-3xl font-extrabold leading-tight sm:text-4xl">
                Hai già una card attiva?
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-cyan-50/85">
                Accedi per gestirla: inserisci la tua email, ricevi il codice
                OTP e aggiorna i dati della tua Cards nell'area dedicata.
              </p>
            </div>
            <a
              href="/gestisci"
              data-track-home-event="home_cta_click"
              data-track-label="Entra nella gestione"
              data-track-source="manage_section"
              data-track-target="/gestisci"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white px-5 py-3 text-sm font-extrabold text-[#13526f] shadow-sm transition hover:bg-cyan-50"
            >
              <Icon icon="mdi:account-key-outline" className="text-lg" />
              Entra nella gestione
            </a>
          </div>
        </section>

        <section id="faq" className="px-5 pb-20">
          <div className="mx-auto max-w-6xl">
            <SectionLabel>Domande frequenti</SectionLabel>
            <h2 className="mt-4 text-3xl font-extrabold leading-tight sm:text-4xl">
              Alcune risposte rapide.
            </h2>
            <div className="mt-8 space-y-3">
              {faqs.map(([question, answer], index) => (
                <details
                  key={question}
                  onToggle={(event) => {
                    if (event.currentTarget.open) {
                      trackHomeEvent("home_faq_open", {
                        label: "FAQ",
                        faqIndex: index + 1,
                      });
                    }
                  }}
                  className="group rounded-lg bg-white px-5 py-4 shadow-sm ring-1 ring-slate-200/70"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-extrabold text-slate-800">
                    {question}
                    <Icon
                      icon="mdi:chevron-down"
                      className="shrink-0 text-lg text-slate-400 transition group-open:rotate-180"
                    />
                  </summary>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    {answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="px-5 pb-20">
          <div className="mx-auto max-w-6xl rounded-lg bg-gradient-to-br from-[#164b66] to-[#2298a4] px-6 py-12 text-center text-white shadow-xl shadow-cyan-950/20">
            <h2 className="text-3xl font-extrabold">
              Pronto ad attivare la tua Cards?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-cyan-50/80">
              Registra la tua Cards o portala sempre con te: un biglietto da
              visita digitale più ordinato, veloce e professionale.
            </p>
            <a
              href="#iscriviti"
              data-track-home-event="home_cta_click"
              data-track-label="Apri profilo gratis"
              data-track-source="final_cta"
              data-track-target="#iscriviti"
              className="mt-7 inline-flex rounded-md bg-white px-5 py-3 text-sm font-extrabold text-[#13526f] transition hover:bg-cyan-50"
            >
              Apri profilo gratis
            </a>
          </div>
        </section>
      </main>

      <footer className="bg-[#123d54] px-5 py-10 text-white">
        <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-2 lg:grid-cols-[1.2fr_1fr_0.85fr_0.9fr]">
          <div>
            <img src={cardsWhiteLogoUrl} alt="Cards" className="h-10 w-auto" />
            <p className="mt-4 text-xs leading-5 text-cyan-50/75">
              Cards è il servizio di biglietti da visita digitali sviluppato nel
              contesto di OnoranzeFunebriCloud.
            </p>
          </div>
          <div>
            <h3 className="text-xs font-extrabold uppercase tracking-[0.12em] text-cyan-100">
              Ecosistema OFC
            </h3>
            <div className="mt-4 space-y-4 text-sm text-cyan-50/80">
              <a
                href="https://onoranzefunebricloud.com"
                target="_blank"
                rel="noreferrer"
                className="block transition hover:text-white"
              >
                <span className="font-extrabold text-white transition hover:text-cyan-100">
                  OnoranzeFunebriCloud
                </span>
                <p className="mt-1 text-xs leading-5 text-cyan-50/70">
                  Soluzioni digitali e software verticali per gestire processi,
                  documenti, attività e operatività del settore funebre.
                </p>
                <span className="mt-2 inline-block text-xs font-extrabold text-cyan-100 underline decoration-cyan-100/40 underline-offset-4 transition hover:text-white">
                  Visita il sito
                </span>
              </a>
              <a
                href="https://funeralcloud.it"
                target="_blank"
                rel="noreferrer"
                className="block transition hover:text-white"
              >
                <span className="font-extrabold text-white transition hover:text-cyan-100">
                  FuneralCloud
                </span>
                <p className="mt-1 text-xs leading-5 text-cyan-50/70">
                  Il marketplace che unisce la filiera funebre italiana:
                  imprese, fornitori, servizi e soluzioni specializzate.
                </p>
                <span className="mt-2 inline-block text-xs font-extrabold text-cyan-100 underline decoration-cyan-100/40 underline-offset-4 transition hover:text-white">
                  Visita il sito
                </span>
              </a>
            </div>
          </div>
          <div>
            <h3 className="text-xs font-extrabold uppercase tracking-[0.12em] text-cyan-100">
              Contatti
            </h3>
            <div className="mt-4 space-y-2 text-sm text-cyan-50/80">
              <a
                href="mailto:cards@onoranzefunebricloud.com"
                className="block transition hover:text-white"
              >
                cards@onoranzefunebricloud.com
              </a>
              <a
                href="tel:+390694502976"
                className="block transition hover:text-white"
              >
                +39 06.945.02.976
              </a>
              <p className="pt-2 text-xs leading-5 text-cyan-50/65">
                OFC SRL - Partita IVA 16390181002
              </p>
            </div>
          </div>
          <div>
            <h3 className="text-xs font-extrabold uppercase tracking-[0.12em] text-cyan-100">
              Rimani connesso
            </h3>
            <p className="mt-4 text-xs leading-5 text-cyan-50/70">
              Segui OnoranzeFunebriCloud per aggiornamenti, novità e contenuti
              dedicati alla digitalizzazione del settore funebre.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              {ofcSocialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Segui OnoranzeFunebriCloud su ${social.label}`}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-white/10 text-white ring-1 ring-white/15 transition hover:bg-white hover:text-[#123d54]"
                >
                  <Icon icon={social.icon} className="text-xl" />
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="mx-auto mt-10 flex max-w-6xl flex-col gap-4 border-t border-white/10 pt-6 text-xs text-cyan-50/60 md:flex-row md:items-center md:justify-between">
          <p>
            © 2026 OnoranzeFunebriCloud. Cards - biglietti da visita digitali
            per la filiera funeraria.
          </p>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <a href={termsUrl} className="transition hover:text-white">
              Termini e condizioni
            </a>
            <a href={privacyPolicyUrl} className="transition hover:text-white">
              Privacy Policy
            </a>
            <a href="/sitemap.xml" className="transition hover:text-white">
              Sitemap
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;
